import axios from 'axios';
import qs from 'qs';
import _, {isEmpty} from 'lodash';
import { TEAMMATES_FIRST_LOAD, NOTE_TYPES_FIRST_LOAD, USER_UPDATED_PERMISSIONS, USER_UPDATED_SERVER_VERSION } from "./users_actions";
import { USER_UPDATED_NETWORK_ENABLED } from "./users_actions";
import { USER_UPDATED_INTEGRATIONS } from "./users_actions";
import { USER_UPDATED_CUSTOM_FILTERS } from "./users_actions";
import {USER_UPDATED_BT_CUSTOM_FILTERS} from "./users_actions";
import { initializeFirebase, removeTokenFromUser } from "../notifications/push-notification";
import { fetchNoteTypes } from "./types_actions";

// action types

export const START_USER_LOGIN = 'START_USER_LOGIN';
export const USER_AUTHENTICATED = 'USER_AUTHENTICATED';
export const LOGIN_ERROR = 'LOGIN_ERROR';
export const MFA_REQUIRED = 'MFA_REQUIRED';
export const MFA_CODE_ERROR = 'MFA_CODE_ERROR';
export const MFA_CANCELLED = 'MFA_CANCELLED';

export const START_USER_LOGOUT = 'START_USER_LOGOUT';
export const USER_LOGOUT = 'USER_LOGOUT';
export const LOGOUT_ERROR = 'LOGOUT_ERROR';

export const CURRENT_PAGE_CHANGED = 'CURRENT_PAGE_CHANGED';

// action creators

const CancelToken = axios.CancelToken;
const source = CancelToken.source();

export function userAuthenticated(userSession) {
  return {
    type: USER_AUTHENTICATED,
    userSession: { ...userSession }
  }
}

export function loginError(error) {
  return {
    type: LOGIN_ERROR,
    error
  }
}

export const handleLogin = params => {
  return async dispatch => {
    try {
      dispatch({
        type: START_USER_LOGIN
      });
      const response = await axios.post('/users/sign_in', { user: { ...params } });

      if (response.data.status === 'mfa_required') {
        dispatch({ type: MFA_REQUIRED, pendingToken: response.data.pending_token });
        return { mfaRequired: true };
      }

      dispatch(userAuthenticated(response.data));
      return { mfaRequired: false };
    } catch (e) {
      const errorData = e.response?.data || {};
      dispatch(loginError({
        message: errorData.message || 'Invalid email or password',
        warning: errorData.warning || false,
        remaining_attempts: errorData.remaining_attempts || null
      }));
      return { mfaRequired: false };
    }
  };
};

export const handleVerifyMfaCode = (pendingToken, code) => {
  return async dispatch => {
    try {
      dispatch({ type: START_USER_LOGIN });
      const response = await axios.post('/v1/user/sessions/verify_mfa_code', {
        pending_token: pendingToken,
        code
      });

      if (response.data.status === 'error') {
        dispatch({
          type: MFA_CODE_ERROR,
          error: { reason: response.data.reason, remaining_attempts: response.data.remaining_attempts || null }
        });
        return { ok: false };
      }

      dispatch(userAuthenticated(response.data));
      return { ok: true };
    } catch (e) {
      dispatch({ type: MFA_CODE_ERROR, error: { reason: 'invalid_code', remaining_attempts: null } });
      return { ok: false };
    }
  };
};

export const handleResendMfaCode = pendingToken => {
  return async dispatch => {
    try {
      const response = await axios.post('/v1/user/sessions/resend_mfa_code', { pending_token: pendingToken });

      if (response.data.status === 'error') {
        dispatch({ type: MFA_CODE_ERROR, error: { reason: response.data.reason, remaining_attempts: null } });
        return { ok: false };
      }

      return { ok: true };
    } catch (e) {
      dispatch({ type: MFA_CODE_ERROR, error: { reason: 'cooldown', remaining_attempts: null } });
      return { ok: false };
    }
  };
};

export const handleCancelMfa = () => {
  return dispatch => {
    dispatch({ type: MFA_CANCELLED });
  };
};

export const handleSetSession = (idToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: START_USER_LOGIN
      });

      const response = await fetch("/v1/user/sessions/set_session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`
        }
      });

      const json = await response.json();
      dispatch(userAuthenticated({...json.data}));

    } catch (e) {
      dispatch(loginError('error'));
    }
  };
};

export function userLogout(userSession) {
  return {
    type: USER_LOGOUT,
    userSession
  }
}

export function userUpdateServerVersion(userSession) {
  return {
    type: USER_UPDATED_SERVER_VERSION,
    userSession
  }
}

export function userUpdatePermissions(userSession) {
  return {
    type: USER_UPDATED_PERMISSIONS,
    userSession
  }
}
export const updatePermissions = params => {
  return async dispatch => {
    try {
      dispatch(userUpdatePermissions(params));
    } catch (e) {
    }
  };
};
export function userUpdateNetworkEnabled(userSession) {
  return {
    type: USER_UPDATED_NETWORK_ENABLED,
    userSession
  }
}

export function teammatesFirstLoad(userSession) {
  return {
    type: TEAMMATES_FIRST_LOAD,
    userSession
  }
}

export function noteTypesFirstLoad(userSession) {
  return {
    type: NOTE_TYPES_FIRST_LOAD,
    userSession
  }
}

export function userUpdateIntegrations(userSession) {
  return {
    type: USER_UPDATED_INTEGRATIONS,
    userSession
  }
}

export function userUpdateCustomFilters(custom_filters) {
  return {
    type: USER_UPDATED_CUSTOM_FILTERS,
    custom_filters
  }
}

export function userUpdateBtCustomFilters(custom_filters) {
  return {
    type: USER_UPDATED_BT_CUSTOM_FILTERS,
    custom_filters
  }
}

export const updateServerVersion = params => {
  return async dispatch => {
    try {
      dispatch(userUpdateServerVersion(params));
    } catch (e) {
    }
  };
};

export const updateNetworkEnabled = params => {
  return async dispatch => {
    try {
      dispatch(userUpdateNetworkEnabled(params));
    } catch (e) {
    }
  };
};

export const runTeammatesFirstLoad = userSession => {
  return async dispatch => {
    try {
      const response = await axios.get('/v1/user/teammates/index', {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-User-Email': userSession.email,
          'X-User-Token': userSession.token,
          'X-User-Admin-Log-Token': userSession.admin_auth_token,
        }
      });

      const { teammates } = response.data;
      if (teammates) {
        const keys = Object.keys(teammates);
        const filteredTeammates = keys.reduce((acc, key) => {
          if (!teammates[key].is_extender) {
            acc[key] = teammates[key];
          }
          return acc;
        }, {});
        dispatch(teammatesFirstLoad({ ...userSession, teammates: filteredTeammates, teammatesWithExtender: teammates }));
      } else {
        dispatch(teammatesFirstLoad({ ...userSession, teammates: {}, teammatesWithExtender: {} }));
      }
    } catch (e) {
    }
  };
};

export const runNoteTypesFirstLoad = userSession => {
  return async dispatch => {
    try {
      const response = await axios.get('/v1/user/types/note_types', {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-User-Email': userSession.email,
          'X-User-Token': userSession.token,
          'X-User-Admin-Log-Token': userSession.admin_auth_token,
        }
      });
      dispatch(noteTypesFirstLoad({ ...userSession, note_types: response.data.note_types }));
    } catch (e) {
    }
  };
};

export const updateUserIntegrations = params => {
  return async dispatch => {
    try {
      dispatch(userUpdateIntegrations(params));
    } catch (e) {
    }
  };
};

export const updateUserCustomFilters = params => {
  return async dispatch => {
    try {
      dispatch(userUpdateCustomFilters(params));
    } catch (e) {
    }
  };
};

export const updateUserBtCustomFilters = params => {
    return async dispatch => {
        try {
            dispatch(userUpdateBtCustomFilters(params));
        } catch (e) {
        }
    };
};

export const handleLogout = params => {
  return async dispatch => {
    try {
      dispatch({
        type: START_USER_LOGOUT
      });

      removeTokenFromUser(params)
      const response = await axios.delete('/users/sign_out', { params: { impersonated_by: params.impersonated_by }, headers: { 'X-User-Email': params.email, 'X-User-Token': params.token } });
      dispatch(userLogout(response.data));
    } catch (e) {
      dispatch(userAuthenticated(params));
    }
  };
};

export function pageChanged(page) {
  return {
    type: CURRENT_PAGE_CHANGED,
    currentPage: page
  }
};

export const handlePageChange = (page) => {
  return dispatch => {
    dispatch(pageChanged(page))
  }
};

export const SAVE_MAIN_LEFT_SIDEBAR_STATUS = 'SAVE_MAIN_LEFT_SIDEBAR_STATUS';

export function saveMainLeftSidebarStatus(mainLeftSidebarStatus) {
  return {
    type: SAVE_MAIN_LEFT_SIDEBAR_STATUS,
    mainLeftSidebarStatus,
  }
}

export const handleSaveMainLeftSidebarStatus = (mainLeftSidebarStatus) => {
  return dispatch => {
    dispatch(saveMainLeftSidebarStatus(mainLeftSidebarStatus))
  }
}

export const FETCHING_FAMILY_OFFICE = 'FETCHING_FAMILY_OFFICE';
export const FAMILY_OFFICE_FETCHED = 'FAMILY_OFFICE_FETCHED';
export const ERROR_FETCHING_FAMILY_OFFICE = 'ERROR_FETCHING_FAMILY_OFFICE';
export const FETCHING_RELATED_FAMILY_OFFICE = 'FETCHING_RELATED_FAMILY_OFFICE';
export const RELATED_FAMILY_OFFICE_FETCHED = 'RELATED_FAMILY_OFFICE_FETCHED';
export const ERROR_FETCHING_RELATED_FAMILY_OFFICE = 'ERROR_FETCHING_RELATED_FAMILY_OFFICE';


export function fetchFamilyOffice(response) {
  return {
    type: FAMILY_OFFICE_FETCHED,
    familyOffice: response
  }
}

export const handleFetchFamilyOffice = (session, cancelToken, familyOfficeId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FAMILY_OFFICE,
        familyOfficeId: familyOfficeId
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOffice(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FAMILY_OFFICE,
        familyOffice: {
          status: 'error',
          message: 'Error fetching data'
        }
      });
    }
  };
}

export const FETCHING_FAMILY_OFFICE_SECTOR_EXPOSURE = 'FETCHING_FAMILY_OFFICE_SECTOR_EXPOSURE';
export const FAMILY_OFFICE_SECTOR_EXPOSURE_FETCHED = 'FAMILY_OFFICE_SECTOR_EXPOSURE_FETCHED';
export const ERROR_FETCHING_FAMILY_OFFICE_SECTOR_EXPOSURE = 'ERROR_FETCHING_FAMILY_OFFICE_SECTOR_EXPOSURE';

export function fetchFamilyOfficeSectorExposure(response) {
  return {
    type: FAMILY_OFFICE_SECTOR_EXPOSURE_FETCHED,
    familyOfficeSectorExposure: response
  }
}

export const handleFetchFamilyOfficeSectorExposure = (session, familyOfficeId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FAMILY_OFFICE_SECTOR_EXPOSURE
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/sector_exposure`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeSectorExposure(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FAMILY_OFFICE_SECTOR_EXPOSURE,
        familyOfficeSectorExposure: {
          status: 'error',
          message: 'Error fetching sector exposure'
        }
      });
    }
  };
}

export const FETCHING_FAMILY_OFFICE_DIRECT_INVESTMENTS = 'FETCHING_FAMILY_OFFICE_DIRECT_INVESTMENTS';
export const FAMILY_OFFICE_DIRECT_INVESTMENTS_FETCHED = 'FAMILY_OFFICE_DIRECT_INVESTMENTS_FETCHED';
export const ERROR_FETCHING_FAMILY_OFFICE_DIRECT_INVESTMENTS = 'ERROR_FETCHING_FAMILY_OFFICE_DIRECT_INVESTMENTS';

export function fetchFamilyOfficeDirectInvestments(response) {
  return {
    type: FAMILY_OFFICE_DIRECT_INVESTMENTS_FETCHED,
    familyOfficeDirectInvestments: response
  }
}

export const handleFetchFamilyOfficeDirectInvestments = (session, cancelToken, familyOfficeId, params) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FAMILY_OFFICE_DIRECT_INVESTMENTS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/direct_investments`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeDirectInvestments(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FAMILY_OFFICE_DIRECT_INVESTMENTS,
        familyOfficeDirectInvestments: {
          status: 'error',
          message: 'Error fetching direct investments'
        }
      });
    }
  };
}


export const FETCHING_FAMILY_OFFICE_DIRECT_REAL_ESTATE_INVESTMENTS = 'FETCHING_FAMILY_OFFICE_DIRECT_REAL_ESTATE_INVESTMENTS';
export const FAMILY_OFFICE_DIRECT_REAL_ESTATE_INVESTMENTS_FETCHED = 'FAMILY_OFFICE_DIRECT_REAL_ESTATE_INVESTMENTS_FETCHED';
export const ERROR_FETCHING_FAMILY_OFFICE_DIRECT_REAL_ESTATE_INVESTMENTS = 'ERROR_FETCHING_FAMILY_OFFICE_DIRECT_REAL_ESTATE_INVESTMENTS';

export function fetchFamilyOfficeDirectRealEstateInvestments(response) {
  console.log(response)
  return {
    type: FAMILY_OFFICE_DIRECT_REAL_ESTATE_INVESTMENTS_FETCHED,
    familyOfficeDirectRealEstateInvestments: response
  }
}

export const handleFetchFamilyOfficeRealEstateDirectInvestments = (session, cancelToken, familyOfficeId, params) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FAMILY_OFFICE_DIRECT_REAL_ESTATE_INVESTMENTS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/direct_real_estate_investments`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeDirectRealEstateInvestments(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FAMILY_OFFICE_DIRECT_REAL_ESTATE_INVESTMENTS,
        familyOfficeDirectRealEstateInvestments: {
          status: 'error',
          message: 'Error fetching direct real estate investments'
        }
      });
    }
  };
}


export const FETCHING_FAMILY_OFFICE_SIMILAR_INVESTMENTS = 'FETCHING_FAMILY_OFFICE_SIMILAR_INVESTMENTS';
export const FAMILY_OFFICE_SIMILAR_INVESTMENTS_FETCHED = 'FAMILY_OFFICE_SIMILAR_INVESTMENTS_FETCHED';
export const ERROR_FETCHING_FAMILY_OFFICE_SIMILAR_INVESTMENTS = 'ERROR_FETCHING_FAMILY_OFFICE_SIMILAR_INVESTMENTS';

export function fetchFamilyOfficeSimilarInvestments(response) {
  return {
    type: FAMILY_OFFICE_SIMILAR_INVESTMENTS_FETCHED,
    familyOfficeSimilarInvestments: response
  }
}

export const handleFetchFamilyOfficeSimilarInvestments = (session, familyOfficeId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FAMILY_OFFICE_SIMILAR_INVESTMENTS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/family_offices_similar_investments`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeSimilarInvestments(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FAMILY_OFFICE_SIMILAR_INVESTMENTS,
        familyOfficeSimilarInvestments: {
          status: 'error',
          message: 'Error fetching family offices with similar investments'
        }
      });
    }
  };
}

export const FETCHING_FAMILY_OFFICE_NEWS_ACTIVITY = 'FETCHING_FAMILY_OFFICE_NEWS_ACTIVITY';
export const FAMILY_OFFICE_NEWS_ACTIVITY_FETCHED = 'FAMILY_OFFICE_NEWS_ACTIVITY_FETCHED';
export const ERROR_FETCHING_FAMILY_OFFICE_NEWS_ACTIVITY = 'ERROR_FETCHING_FAMILY_OFFICE_NEWS_ACTIVITY';

export function fetchFamilyOfficeNewsActivity(response) {
  return {
    type: FAMILY_OFFICE_NEWS_ACTIVITY_FETCHED,
    familyOfficeNewsActivity: response
  }
}

export const handleFetchFamilyOfficeNewsActivity = (session, familyOfficeId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FAMILY_OFFICE_NEWS_ACTIVITY
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/news_activity`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeNewsActivity(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FAMILY_OFFICE_NEWS_ACTIVITY,
        familyOfficeNewsActivity: {
          status: 'error',
          message: 'Error fetching news'
        }
      });
    }
  };
}

export const FETCHING_FAMILY_OFFICE_NEWS_NEW_HIRES = 'FETCHING_FAMILY_OFFICE_NEWS_NEW_HIRES';
export const FAMILY_OFFICE_NEWS_NEW_HIRES_FETCHED = 'FAMILY_OFFICE_NEWS_NEW_HIRES_FETCHED';
export const ERROR_FETCHING_FAMILY_OFFICE_NEWS_NEW_HIRES = 'ERROR_FETCHING_FAMILY_OFFICE_NEWS_NEW_HIRES';

export function fetchFamilyOfficeNewHiresNews(response) {
  return {
    type: FAMILY_OFFICE_NEWS_NEW_HIRES_FETCHED,
    familyOfficeNewHiresNews: response
  }
}

export const handleFetchFamilyOfficeNewHiresNews = (session, familyOfficeId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FAMILY_OFFICE_NEWS_NEW_HIRES
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/new_hires`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeNewHiresNews(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FAMILY_OFFICE_NEWS_NEW_HIRES,
        familyOfficeNewHiresNews: {
          status: 'error',
          message: 'Error fetching news'
        }
      });
    }
  };
}

export const FETCHING_FAMILY_OFFICE_NEWS_INVESTMENT_NEWS = 'FETCHING_FAMILY_OFFICE_NEWS_INVESTMENT_NEWS';
export const FAMILY_OFFICE_NEWS_INVESTMENT_NEWS_FETCHED = 'FAMILY_OFFICE_NEWS_INVESTMENT_NEWS_FETCHED';
export const ERROR_FETCHING_FAMILY_OFFICE_NEWS_INVESTMENT_NEWS = 'ERROR_FETCHING_FAMILY_OFFICE_NEWS_INVESTMENT_NEWS';

export function fetchFamilyOfficeInvestmentNews(response) {
  return {
    type: FAMILY_OFFICE_NEWS_INVESTMENT_NEWS_FETCHED,
    familyOfficeInvestmentNews: response
  }
}

export const handleFetchFamilyOfficeInvestmentNews = (session, familyOfficeId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FAMILY_OFFICE_NEWS_INVESTMENT_NEWS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/investment_news`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeInvestmentNews(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FAMILY_OFFICE_NEWS_INVESTMENT_NEWS,
        familyOfficeInvestmentNews: {
          status: 'error',
          message: 'Error fetching news'
        }
      });
    }
  };
}

export const FETCHING_FAMILY_OFFICE_NEWS_GENERAL = 'FETCHING_FAMILY_OFFICE_NEWS_GENERAL';
export const FAMILY_OFFICE_NEWS_GENERAL_FETCHED = 'FAMILY_OFFICE_NEWS_GENERAL_FETCHED';
export const ERROR_FETCHING_FAMILY_OFFICE_NEWS_GENERAL = 'ERROR_FETCHING_FAMILY_OFFICE_NEWS_GENERAL';

export function fetchFamilyOfficeGeneralNews(response) {
  return {
    type: FAMILY_OFFICE_NEWS_GENERAL_FETCHED,
    familyOfficeGeneralNews: response
  }
}

export const handleFetchFamilyOfficeGeneralNews = (session, familyOfficeId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FAMILY_OFFICE_NEWS_GENERAL
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/general_news`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeGeneralNews(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FAMILY_OFFICE_NEWS_GENERAL,
        familyOfficeGeneralNews: {
          status: 'error',
          message: 'Error fetching news'
        }
      });
    }
  };
}

export const FETCHING_FO_CONTACT = 'FETCHING_FO_CONTACT';
export const FO_CONTACT_FETCHED = 'FO_CONTACT_FETCHED';
export const ERROR_FETCHING_FO_CONTACT = 'ERROR_FETCHING_FO_CONTACT';

export function fetchContact(response) {
  return {
    type: FO_CONTACT_FETCHED,
    contact: response
  }
}

export const handleFetchContact = (session, contactId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FO_CONTACT,
        contactId: contactId
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchContact(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FO_CONTACT,
        contact: {
          status: 'error',
          message: 'Error fetching contact'
        }
      });
    }
  };
}

export const FETCHING_CONTACT_PASSIONS = 'FETCHING_CONTACT_PASSIONS';
export const CONTACT_PASSIONS_FETCHED = 'CONTACT_PASSIONS_FETCHED';
export const ERROR_FETCHING_CONTACT_PASSIONS = 'ERROR_FETCHING_CONTACT_PASSIONS';

export function fetchContactPassionsInterests(response, contactId) {
  return {
    type: CONTACT_PASSIONS_FETCHED,
    contactPassionsInterests: response,
    contactId

  }
}

export const handleFetchContactPassionsInterests = (session, contactId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_CONTACT_PASSIONS,
        contactId
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}/passions_and_interests`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchContactPassionsInterests(response.data, contactId));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_CONTACT_PASSIONS,
        contactPassionsInterests: {
          status: 'error',
          message: 'Error fetching contact Passions and Interests'
        }
      });
    }
  };
}

export const FETCHING_CONTACT_TASKS = 'FETCHING_CONTACT_TASKS';
export const CONTACT_TASKS_FETCHED = 'CONTACT_TASKS_FETCHED';
export const ERROR_FETCHING_CONTACT_TASKS = 'ERROR_FETCHING_CONTACT_TASKS';

export function fetchContactTasks(response) {
  return {
    type: CONTACT_TASKS_FETCHED,
    contactTasks: response
  }
}

export const handleFetchContactTasks = (session, cancelToken, contactId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_CONTACT_TASKS
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}/tasks`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchContactTasks(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_CONTACT_TASKS,
        contactTasks: {
          status: 'error',
          message: 'Error fetching contact Tasks'
        }
      });
    }
  };
}

export const FETCHING_CONTACT_NOTES = 'FETCHING_CONTACT_NOTES';
export const CONTACT_NOTES_FETCHED = 'CONTACT_NOTES_FETCHED';
export const ERROR_FETCHING_CONTACT_NOTES = 'ERROR_FETCHING_CONTACT_NOTES';

export function fetchContactNotes(response) {
  return {
    type: CONTACT_NOTES_FETCHED,
    contactNotes: response
  }
}

export const handleFetchContactNotes = (session, cancelToken, contactId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_CONTACT_NOTES
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}/notes`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchContactNotes(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_CONTACT_NOTES,
        contactNotes: {
          status: 'error',
          message: 'Error fetching contact Notes'
        }
      });
    }
  };
}

export const FETCHING_CONTACT_NEWS = 'FETCHING_CONTACT_NEWS';
export const CONTACT_NEWS_FETCHED = 'CONTACT_NEWS_FETCHED';
export const ERROR_FETCHING_CONTACT_NEWS = 'ERROR_FETCHING_CONTACT_NEWS';

export function fetchContactNews(response) {
  return {
    type: CONTACT_NEWS_FETCHED,
    contactNews: response
  }
}

export const handleFetchContactNews = (session, contactId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_CONTACT_NEWS
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}/news`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchContactNews(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_CONTACT_NEWS,
        contactNews: {
          status: 'error',
          message: 'Error fetching contact News'
        }
      });
    }
  };
}

export const FETCHING_CONTACT_LISTS = 'FETCHING_CONTACT_LISTS';
export const CONTACT_LISTS_FETCHED = 'CONTACT_LISTS_FETCHED';
export const ERROR_FETCHING_CONTACT_LISTS = 'ERROR_FETCHING_CONTACT_LISTS';

export function fetchContactLists(response) {
  return {
    type: CONTACT_LISTS_FETCHED,
    contactLists: response
  }
}

export const handleFetchContactLists = (session, cancelToken, contactId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_CONTACT_LISTS
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}/lists`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchContactLists(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_CONTACT_LISTS,
        contactLists: {
          status: 'error',
          message: 'Error fetching contact Lists'
        }
      });
    }
  };
}

export const FETCHING_CONTACT_EMPLOYMENT = 'FETCHING_CONTACT_EMPLOYMENT';
export const CONTACT_EMPLOYMENT_FETCHED = 'CONTACT_EMPLOYMENT_FETCHED';
export const ERROR_FETCHING_CONTACT_EMPLOYMENT = 'ERROR_FETCHING_CONTACT_EMPLOYMENT';

export function fetchContactEmploymentHistory(response, contactId) {
  return {
    type: CONTACT_EMPLOYMENT_FETCHED,
    contactEmploymentHistory: response,
    contactId
  }
}

export const handleFetchContactEmploymentHistory = (session, contactId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_CONTACT_EMPLOYMENT,
        contactId
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}/employment_history`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchContactEmploymentHistory(response.data, contactId));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_CONTACT_EMPLOYMENT,
        contactEmploymentHistory: {
          status: 'error',
          message: 'Error fetching contact Employment History'
        }
      });
    }
  };
}

export const FETCHING_CONTACT_EDUCATION = 'FETCHING_CONTACT_EDUCATION';
export const CONTACT_EDUCATION_FETCHED = 'CONTACT_EDUCATION_FETCHED';
export const ERROR_FETCHING_CONTACT_EDUCATION = 'ERROR_FETCHING_CONTACT_EDUCATION';

export function fetchContactEducation(response, contactId) {
  return {
    type: CONTACT_EDUCATION_FETCHED,
    contactEducation: response,
    contactId
  }
}

export const handleFetchContactEducation = (session, contactId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_CONTACT_EDUCATION,
        contactId
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}/education`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchContactEducation(response.data, contactId));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_CONTACT_EDUCATION,
        contactEducation: {
          status: 'error',
          message: 'Error fetching contact Education'
        }
      });
    }
  };
}

export const FETCHING_FO_LISTS = 'FETCHING_FO_LISTS';
export const FO_LISTS_FETCHED = 'FO_LISTS_FETCHED';
export const ERROR_FETCHING_FO_LISTS = 'ERROR_FETCHING_FO_LISTS';

export function fetchFamilyOfficeLists(response) {
  return {
    type: FO_LISTS_FETCHED,
    familyOfficeLists: response
  }
}

export const handleFetchFamilyOfficeLists = (session, cancelToken, familyOfficeId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FO_LISTS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/lists`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeLists(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FO_LISTS,
        familyOfficeLists: {
          status: 'error',
          message: 'Error fetching family Office Lists'
        }
      });
    }
  };
}

export const FETCHING_FUNDED_COMPANY = 'FETCHING_FUNDED_COMPANY';
export const FUNDED_COMPANY_FETCHED = 'FUNDED_COMPANY_FETCHED';
export const ERROR_FETCHING_FUNDED_COMPANY = 'ERROR_FETCHING_FUNDED_COMPANY';

export function fetchFundedCompany(response) {
  return {
    type: FUNDED_COMPANY_FETCHED,
    fundedCompany: response
  }
}

export const handleFetchFundedCompany = (session, fundedCompanyId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FUNDED_COMPANY
      });

      const response = await axios.get(
        `/v1/user/funded_companies/${fundedCompanyId}`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFundedCompany(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FUNDED_COMPANY,
        fundedCompany: {
          status: 'error',
          message: 'Error fetching Funded Company'
        }
      });
    }
  };
}

export const FETCHING_FO_AVAILABLE_CUSTOM_PROPS = 'FETCHING_FO_AVAILABLE_CUSTOM_PROPS';
export const FO_AVAILABLE_CUSTOM_PROPS_FETCHED = 'FO_AVAILABLE_CUSTOM_PROPS_FETCHED';
export const ERROR_FETCHING_FO_AVAILABLE_CUSTOM_PROPS = 'ERROR_FETCHING_FO_AVAILABLE_CUSTOM_PROPS';

export function fetchFamilyOfficeAvailableCustomProperties(response) {
  return {
    type: FO_AVAILABLE_CUSTOM_PROPS_FETCHED,
    familyOfficeAvailableCustomProperties: response
  }
}

export const handleFetchFamilyOfficeAvailableCustomProperties = (session, familyOfficeId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FO_AVAILABLE_CUSTOM_PROPS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/available_properties`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeAvailableCustomProperties(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FO_AVAILABLE_CUSTOM_PROPS,
        familyOfficeAvailableCustomProperties: {
          status: 'error',
          message: 'Error fetching family Office Custom Properties'
        }
      });
    }
  };
}

export const FETCHING_FO_TASKS = 'FETCHING_FO_TASKS';
export const FO_TASKS_FETCHED = 'FO_TASKS_FETCHED';
export const ERROR_FETCHING_FO_TASKS = 'ERROR_FETCHING_FO_TASKS';

export function fetchFamilyOfficeTasks(response) {
  return {
    type: FO_TASKS_FETCHED,
    familyOfficeTasks: response
  }
}

export const handleFetchFamilyOfficeTasks = (session, cancelToken, familyOfficeId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FO_TASKS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/tasks`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeTasks(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FO_TASKS,
        familyOfficeTasks: {
          status: 'error',
          message: 'Error fetching family Office Tasks'
        }
      });
    }
  };
}

export const FETCHING_FO_NOTES = 'FETCHING_FO_NOTES';
export const FO_NOTES_FETCHED = 'FO_NOTES_FETCHED';
export const ERROR_FETCHING_FO_NOTES = 'ERROR_FETCHING_FO_NOTES';

export function fetchFamilyOfficeNotes(response) {
  return {
    type: FO_NOTES_FETCHED,
    familyOfficeNotes: response
  }
}

export const handleFetchFamilyOfficeNotes = (session, cancelToken, familyOfficeId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FO_NOTES
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/notes`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeNotes(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FO_NOTES,
        familyOfficeNotes: {
          status: 'error',
          message: 'Error fetching family Office Notes'
        }
      });
    }
  };
}

export const FETCHING_TRANSACTIONS_DETAILS = 'FETCHING_TRANSACTIONS_DETAILS';
export const TRANSACTIONS_DETAILS_FETCHED = 'TRANSACTIONS_DETAILS_FETCHED';
export const ERROR_FETCHING_TRANSACTIONS_DETAILS = 'ERROR_FETCHING_TRANSACTIONS_DETAILS';

export function fetchTransactionDetails(response) {
  return {
    type: TRANSACTIONS_DETAILS_FETCHED,
    transactionsDetails: response
  }
}

export const handleFetchFamilyOfficeTransactionDetails = (session, familyOfficeId, riaCrdId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_TRANSACTIONS_DETAILS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/transactions_details`,
        {
          params: riaCrdId ? { ria_crd_id: riaCrdId } : {},
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchTransactionDetails(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_TRANSACTIONS_DETAILS,
        transactionsDetails: {
          status: 'error',
          message: 'Error fetching transactions Details'
        }
      });
    }
  };
}

export const FETCHING_FO_TRACKED_INVESTMENTS = 'FETCHING_FO_TRACKED_INVESTMENTS';
export const FO_TRACKED_INVESTMENTS_FETCHED = 'FO_TRACKED_INVESTMENTS_FETCHED';
export const ERROR_FETCHING_FO_TRACKED_INVESTMENTS = 'ERROR_FETCHING_FO_TRACKED_INVESTMENTS';

export function fetchFamilyOfficeTrackedInvestments(response) {
  return {
    type: FO_TRACKED_INVESTMENTS_FETCHED,
    familyOfficeTrackedInvestments: response
  }
}

export const handleFetchFamilyOfficeTrackedInvestments = (session, cancelToken, familyOfficeId, params, riaCrdId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FO_TRACKED_INVESTMENTS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/direct_investments`,
        {
          params: riaCrdId ? { ...params, ria_crd_id: riaCrdId } : params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeTrackedInvestments(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FO_TRACKED_INVESTMENTS,
        familyOfficeTrackedInvestments: {
          status: 'error',
          message: 'Error fetching tracked investments'
        }
      });
    }
  };
}

export const FETCHING_FO_INV_INDUSTRIES_ROUNDS = 'FETCHING_FO_INV_INDUSTRIES_ROUNDS';
export const FO_INV_INDUSTRIES_ROUNDS_FETCHED = 'FO_INV_INDUSTRIES_ROUNDS_FETCHED';
export const ERROR_FETCHING_FO_INV_INDUSTRIES_ROUNDS = 'ERROR_FETCHING_FO_INV_INDUSTRIES_ROUNDS';

export function fetchFamilyOfficeInvestmentIndustriesRounds(response) {
  return {
    type: FO_INV_INDUSTRIES_ROUNDS_FETCHED,
    familyOfficeInvestmentIndustriesRounds: response
  }
}

export const handleFetchFamilyOfficeInvestmentIndustriesRounds = (session, cancelToken, familyOfficeId, riaCrdId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FO_INV_INDUSTRIES_ROUNDS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/investment_industries_rounds`,
        {
          params: riaCrdId ? { ria_crd_id: riaCrdId } : {},
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeInvestmentIndustriesRounds(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FO_INV_INDUSTRIES_ROUNDS,
        familyOfficeInvestmentIndustriesRounds: {
          status: 'error',
          message: 'Error fetching investment industries and rounds'
        }
      });
    }
  };
}

export const FETCHING_FO_AUM_ACCOUNTS = 'FETCHING_FO_AUM_ACCOUNTS';
export const FO_AUM_ACCOUNTS_FETCHED = 'FO_AUM_ACCOUNTS_FETCHED';
export const ERROR_FETCHING_FO_AUM_ACCOUNTS = 'ERROR_FETCHING_FO_AUM_ACCOUNTS';

export function fetchFamilyOfficeAumAndAccounts(response) {
  return {
    type: FO_AUM_ACCOUNTS_FETCHED,
    familyOfficeAumAndAccounts: response
  }
}

export const handleFetchFamilyOfficeAumAndAccounts = (session, familyOfficeId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FO_AUM_ACCOUNTS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/aum_accounts`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeAumAndAccounts(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FO_AUM_ACCOUNTS,
        familyOfficeAumAndAccounts: {
          status: 'error',
          message: 'Error fetching aum and accounts'
        }
      });
    }
  };
}

export const FETCHING_FO_ALL_NEWS = 'FETCHING_FO_ALL_NEWS';
export const FO_ALL_NEWS_FETCHED = 'FO_ALL_NEWS_FETCHED';
export const ERROR_FETCHING_FO_ALL_NEWS = 'ERROR_FETCHING_FO_ALL_NEWS';

export function fetchFamilyOfficeAllNews(response) {
  return {
    type: FO_ALL_NEWS_FETCHED,
    familyOfficeAllNews: response
  }
}

export const handleFetchFamilyOfficeAllNews = (session, familyOfficeId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FO_ALL_NEWS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/all_news`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeAllNews(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FO_ALL_NEWS,
        familyOfficeAllNews: {
          status: 'error',
          message: 'Error fetching investor news'
        }
      });
    }
  };
}

export const FETCHING_CONTACT_ALL_NEWS = 'FETCHING_CONTACT_ALL_NEWS';
export const CONTACT_ALL_NEWS_FETCHED = 'CONTACT_ALL_NEWS_FETCHED';
export const ERROR_FETCHING_CONTACT_ALL_NEWS = 'ERROR_FETCHING_CONTACT_ALL_NEWS';

export function fetchContactAllNews(response) {
  return {
    type: CONTACT_ALL_NEWS_FETCHED,
    contactAllNews: response
  }
}

export const handleFetchContactAllNews = (session, contactId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_CONTACT_ALL_NEWS
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}/news`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: source.token
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchContactAllNews(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_CONTACT_ALL_NEWS,
        contactAllNews: {
          status: 'error',
          message: 'Error fetching contact news'
        }
      });
    }
  };
}

export const FETCHING_CONTACT_TEAMMATES_AFFINITY_SCORE = 'FETCHING_CONTACT_TEAMMATES_AFFINITY_SCORE';
export const CONTACT_TEAMMATES_AFFINITY_SCORE_FETCHED = 'CONTACT_TEAMMATES_AFFINITY_SCORE_FETCHED';
export const ERROR_FETCHING_CONTACT_TEAMMATES_AFFINITY_SCORE = 'ERROR_FETCHING_CONTACT_TEAMMATES_AFFINITY_SCORE';

export function fetchContactTeammatesAffinityScore(response) {
  return {
    type: CONTACT_TEAMMATES_AFFINITY_SCORE_FETCHED,
    contactTeammatesAffinityScore: response
  }
}

export const handleFetchContactTeammatesAffinityScore = (session, cancelToken, contactId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_CONTACT_TEAMMATES_AFFINITY_SCORE
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}/teammates_affinity_score`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchContactTeammatesAffinityScore(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_CONTACT_TEAMMATES_AFFINITY_SCORE,
        contactTeammatesAffinityScore: {
          status: 'error',
          message: 'Error fetching contact affinity score'
        }
      });
    }
  };
}

export const FETCHING_TEAMMATE_AFFINITY_SCORE = 'FETCHING_TEAMMATE_AFFINITY_SCORE';
export const TEAMMATE_AFFINITY_SCORE_FETCHED = 'TEAMMATE_AFFINITY_SCORE_FETCHED';
export const ERROR_FETCHING_TEAMMATE_AFFINITY_SCORE = 'ERROR_FETCHING_TEAMMATE_AFFINITY_SCORE';

export function fetchContactTeammateAffinityScore(response) {
  return {
    type: TEAMMATE_AFFINITY_SCORE_FETCHED,
    contactTeammateAffinityScore: response
  }
}

export const handleFetchTeammateContactAffinityScore = (session, cancelToken, contactId, teammateId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_TEAMMATE_AFFINITY_SCORE
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}/teammate_affinity_score/${teammateId}`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchContactTeammateAffinityScore(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_TEAMMATE_AFFINITY_SCORE,
        contactTeammateAffinityScore: {
          status: 'error',
          message: 'Error fetching teammate affinity score'
        }
      });
    }
  };
}

export const FETCHING_SIDEBAR_CONTACT_TEAMMATES_AFFINITY_SCORE = 'FETCHING_SIDEBAR_CONTACT_TEAMMATES_AFFINITY_SCORE';
export const SIDEBAR_CONTACT_TEAMMATES_AFFINITY_SCORE_FETCHED = 'SIDEBAR_CONTACT_TEAMMATES_AFFINITY_SCORE_FETCHED';
export const ERROR_FETCHING_SIDEBAR_CONTACT_TEAMMATES_AFFINITY_SCORE = 'ERROR_FETCHING_SIDEBAR_CONTACT_TEAMMATES_AFFINITY_SCORE';

export function fetchSidebarContactTeammatesAffinityScore(response, contactId) {
  return {
    type: SIDEBAR_CONTACT_TEAMMATES_AFFINITY_SCORE_FETCHED,
    contactSidebarTeammatesAffinityScore: response,
    contactId: contactId
  }
}

export const handleFetchSidebarContactTeammatesAffinityScore = (session, cancelToken, contactId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_SIDEBAR_CONTACT_TEAMMATES_AFFINITY_SCORE,
        contactId
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}/teammates_affinity_score`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchSidebarContactTeammatesAffinityScore(response.data, contactId));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_SIDEBAR_CONTACT_TEAMMATES_AFFINITY_SCORE,
        contactSidebarTeammatesAffinityScore: {
          status: 'error',
          message: 'Error fetching contact affinity score'
        }
      });
    }
  };
}

export const FETCHING_SIDEBAR_TEAMMATE_AFFINITY_SCORE = 'FETCHING_SIDEBAR_TEAMMATE_AFFINITY_SCORE';
export const SIDEBAR_TEAMMATE_AFFINITY_SCORE_FETCHED = 'SIDEBAR_TEAMMATE_AFFINITY_SCORE_FETCHED';
export const ERROR_FETCHING_SIDEBAR_TEAMMATE_AFFINITY_SCORE = 'ERROR_FETCHING_SIDEBAR_TEAMMATE_AFFINITY_SCORE';

export function fetchSidebarContactTeammateAffinityScore(response, contactId) {
  return {
    type: SIDEBAR_TEAMMATE_AFFINITY_SCORE_FETCHED,
    contactSidebarTeammateAffinityScore: response,
    contactId
  }
}

export const handleFetchSidebarTeammateContactAffinityScore = (session, cancelToken, contactId, teammateId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_SIDEBAR_TEAMMATE_AFFINITY_SCORE,
        contactId
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}/teammate_affinity_score/${teammateId}`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchSidebarContactTeammateAffinityScore(response.data, contactId));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_SIDEBAR_TEAMMATE_AFFINITY_SCORE,
        contactSidebarTeammateAffinityScore: {
          status: 'error',
          message: 'Error fetching teammate affinity score'
        }
      });
    }
  };
}

export const FETCHING_INVESTOR_OPTIONS = 'FETCHING_INVESTOR_OPTIONS';
export const INVESTOR_OPTIONS_FETCHED = 'INVESTOR_OPTIONS_FETCHED';
export const ERROR_FETCHING_INVESTOR_OPTIONS = 'ERROR_FETCHING_INVESTOR_OPTIONS';

export function fetchInvestorsOptions(response) {
  return {
    type: INVESTOR_OPTIONS_FETCHED,
    investorsOptions: response
  }
}

export const handleFetchInvestorsOptions = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_INVESTOR_OPTIONS
      });

      const response = await axios.get(
        `/v1/user/power_search/investors_options`,
        {
          params,
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchInvestorsOptions(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_INVESTOR_OPTIONS,
        investorsOptions: {
          status: 'error',
          message: 'Error fetching investors options for search'
        }
      });
    }
  };
}

export const FETCHING_CONTACT_OPTIONS = 'FETCHING_CONTACT_OPTIONS';
export const CONTACT_OPTIONS_FETCHED = 'CONTACT_OPTIONS_FETCHED';
export const ERROR_FETCHING_CONTACT_OPTIONS = 'ERROR_FETCHING_CONTACT_OPTIONS';

export function fetchContactsOptions(response) {
  return {
    type: CONTACT_OPTIONS_FETCHED,
    contactsOptions: response
  }
}

export const handleFetchContactsOptions = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_CONTACT_OPTIONS
      });

      const response = await axios.get(
        `/v1/user/power_search/contacts_options`,
        {
          params,
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchContactsOptions(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_CONTACT_OPTIONS,
        contactsOptions: {
          status: 'error',
          message: 'Error fetching contacts options for search'
        }
      });
    }
  };
}

export const FETCHING_TRANSACTIONS_OPTIONS = 'FETCHING_TRANSACTIONS_OPTIONS';
export const TRANSACTIONS_OPTIONS_FETCHED = 'TRANSACTIONS_OPTIONS_FETCHED';
export const ERROR_FETCHING_TRANSACTIONS_OPTIONS = 'ERROR_FETCHING_TRANSACTIONS_OPTIONS';

export function fetchTransactionsOptions(response) {
  return {
    type: TRANSACTIONS_OPTIONS_FETCHED,
    transactionsOptions: response
  }
}

export const handleFetchTransactionsOptions = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_TRANSACTIONS_OPTIONS
      });

      const response = await axios.get(
        `/v1/user/power_search/transactions_options`,
        {
          params,
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchTransactionsOptions(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_TRANSACTIONS_OPTIONS,
        transactionsOptions: {
          status: 'error',
          message: 'Error fetching transactions options for search'
        }
      });
    }
  };
}


export const FETCHING_SCORES_OPTIONS = 'FETCHING_SCORES_OPTIONS';
export const SCORES_OPTIONS_FETCHED = 'SCORES_OPTIONS_FETCHED';
export const ERROR_FETCHING_SCORES_OPTIONS = 'ERROR_FETCHING_SCORES_OPTIONS';

export function fetchScoresOptions(response) {
  return {
    type: SCORES_OPTIONS_FETCHED,
    scoresOptions: response
  }
}

export const handleFetchScoresOptions = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_SCORES_OPTIONS
      });

      const response = await axios.get(
        `/v1/user/power_search/scores_options`,
        {
          params,
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchScoresOptions(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_SCORES_OPTIONS,
        scoresOptions: {
          status: 'error',
          message: 'Error fetching scores options for search'
        }
      });
    }
  };
}

export const FETCHING_RIA_SCORES_OPTIONS = 'FETCHING_RIA_SCORES_OPTIONS';
export const RIA_SCORES_OPTIONS_FETCHED = 'RIA_SCORES_OPTIONS_FETCHED';
export const ERROR_FETCHING_RIA_SCORES_OPTIONS = 'ERROR_FETCHING_RIA_SCORES_OPTIONS';

export function fetchRiaScoresOptions(response) {
  return {
    type: RIA_SCORES_OPTIONS_FETCHED,
    riaScoresOptions: response
  }
}

export const handleFetchRiaScoresOptions = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_RIA_SCORES_OPTIONS
      });

      const response = await axios.get(
        `/v1/user/ria_power_search/scores_options`,
        {
          params,
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchRiaScoresOptions(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_RIA_SCORES_OPTIONS,
        riaScoresOptions: {
          status: 'error',
          message: 'Error fetching ria scores options for search'
        }
      });
    }
  };
}

export const FETCHING_SEARCH_FILTERS = 'FETCHING_SEARCH_FILTERS';
export const SEARCH_FILTERS_FETCHED = 'SEARCH_FILTERS_FETCHED';
export const ERROR_FETCHING_SEARCH_FILTERS = 'ERROR_FETCHING_SEARCH_FILTERS';

export function fetchSavedSearchFilters(response) {
  return {
    type: SEARCH_FILTERS_FETCHED,
    savedSearchFilters: response
  }
}

export const handleFetchSavedSearchFilters = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_SEARCH_FILTERS
      });

      const response = await axios.get(
        `/v1/user/power_search/saved_search_filters`,
        {
          params,
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchSavedSearchFilters(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_SEARCH_FILTERS,
        savedSearchFilters: {
          status: 'error',
          message: 'Error fetching saved search filters'
        }
      });
    }
  };
}

export const FETCHING_TEAMMATES_SCORE_CONTACTS = 'FETCHING_TEAMMATES_SCORE_CONTACTS';
export const TEAMMATES_SCORE_CONTACTS_FETCHED = 'TEAMMATES_SCORE_CONTACTS_FETCHED';
export const ERROR_FETCHING_TEAMMATES_SCORE_CONTACTS = 'ERROR_FETCHING_TEAMMATES_SCORE_CONTACTS';

export function fetchTeammatesAffinityScoreContacts(response) {
  return {
    type: TEAMMATES_SCORE_CONTACTS_FETCHED,
    teammatesScoreContacts: response
  }
}

export const handleFetchTeammatesAffinityScoreContacts = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_TEAMMATES_SCORE_CONTACTS
      });

      const response = await axios.get(
        `/v1/user/contacts/teammates_affinity_score_contacts`,
        {
          params,
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchTeammatesAffinityScoreContacts(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_TEAMMATES_SCORE_CONTACTS,
        teammatesScoreContacts: {
          status: 'error',
          message: 'Error fetching teammates scores for contacts'
        }
      });
    }
  };
}

export const FETCHING_SAVED_SEARCH = 'FETCHING_SAVED_SEARCH';
export const SAVED_SEARCH_FETCHED = 'SAVED_SEARCH_FETCHED';
export const ERROR_FETCHING_SAVED_SEARCH = 'ERROR_FETCHING_SAVED_SEARCH';

export function fetchSavedSearch(response) {
  return {
    type: SAVED_SEARCH_FETCHED,
    filterParams: response
  }
}

export const handleFetchSavedSearch = (session, cancelToken, savedSearchId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_SAVED_SEARCH
      });

      const response = await axios.get(
        `/v1/user/power_search/${savedSearchId}/use_saved_search`,
        {
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchSavedSearch(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_SAVED_SEARCH,
        filterParams: {
          status: 'error',
          message: 'Error fetching filter params'
        }
      });
    }
  };
}

export const DELETING_SAVED_SEARCH = 'DELETING_SAVED_SEARCH';
export const SAVED_SEARCH_DELETED = 'SAVED_SEARCH_DELETED';
export const ERROR_DELETING_SAVED_SEARCH = 'ERROR_DELETING_SAVED_SEARCH';

export function deleteSavedSearch(response) {
  return {
    type: SAVED_SEARCH_DELETED,
    savedSearchDeleted: response
  }
}

export const handleDeleteSavedSearch = (session, cancelToken, savedSearchId) => {
  return async dispatch => {
    try {
      dispatch({
        type: DELETING_SAVED_SEARCH
      });

      const response = await axios.delete(
        `/v1/user/power_search/${savedSearchId}`,
        {
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      );

      dispatch(deleteSavedSearch(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_DELETING_SAVED_SEARCH,
        savedSearchDeleted: {
          status: 'error',
          message: 'Error deleting saved search'
        }
      });
    }
  };
}

export const SAVING_KEYWORDS = 'SAVING_KEYWORDS';
export const KEYWORDS_SAVED = 'KEYWORDS_SAVED';
export const ERROR_SAVING_KEYWORDS = 'ERROR_SAVING_KEYWORDS';

export function saveKeywords(response) {
  return {
    type: KEYWORDS_SAVED,
    keywordsSaved: response
  }
}

export const handleSaveKeywords = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: SAVING_KEYWORDS
      });

      const response = await axios.put(
        '/v1/user/power_search/set_keywords',
        qs.stringify({ power_search: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(saveKeywords(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_SAVING_KEYWORDS,
        keywordsSaved: {
          status: 'error',
          message: 'Error saving keywords'
        }
      });
    }
  };
}

export const FETCHING_KEYWORDS = 'FETCHING_KEYWORDS';
export const KEYWORDS_FETCHED = 'KEYWORDS_FETCHED';
export const ERROR_FETCHING_KEYWORDS = 'ERROR_FETCHING_KEYWORDS';

export function lastKeywords(response) {
  return {
    type: KEYWORDS_FETCHED,
    lastKeywords: response
  }
}

export const handleFetchLastKeywords = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_KEYWORDS
      });

      const response = await axios.get(
        '/v1/user/power_search/last_keywords',
        {
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(lastKeywords(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_KEYWORDS,
        lastKeywords: {
          status: 'error',
          message: 'Error fetching keywords'
        }
      });
    }
  };
}

export const CLEARING_KEYWORDS = 'CLEARING_KEYWORDS';
export const KEYWORDS_CLEARED = 'KEYWORDS_CLEARED';
export const ERROR_CLEARING_KEYWORDS = 'ERROR_CLEARING_KEYWORDS';

export function clearKeywords(response) {
  return {
    type: KEYWORDS_CLEARED,
    keywordsCleared: response
  }
}

export const handleClearKeywords = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: CLEARING_KEYWORDS
      });

      const response = await axios.put(
        '/v1/user/power_search/clear_last_keywords',
        {},
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(clearKeywords(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_CLEARING_KEYWORDS,
        keywordsCleared: {
          status: 'error',
          message: 'Error clearing keywords'
        }
      });
    }
  };
}

export const FETCHING_LATEST_VIEWED_RESULTS = 'FETCHING_LATEST_VIEWED_RESULTS';
export const LATEST_VIEWED_RESULTS_FETCHED = 'LATEST_VIEWED_RESULTS_FETCHED';
export const ERROR_FETCHING_LATEST_VIEWED_RESULTS = 'ERROR_FETCHING_LATEST_VIEWED_RESULTS';

export function fetchLatestViewedResults(response) {
  return {
    type: LATEST_VIEWED_RESULTS_FETCHED,
    latestViewedResults: response
  }
}

export const handleFetchLatestViewedResults = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_LATEST_VIEWED_RESULTS
      });

      const response = await axios.get(
        '/v1/user/users/recent_result_viewed',
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchLatestViewedResults(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_LATEST_VIEWED_RESULTS,
        latestViewedResults: {
          status: 'error',
          message: 'Error fetching the recent results viewed'
        }
      });
    }
  };
}

export const TRACKING_LATEST_VIEWED_RESULT = 'TRACKING_LATEST_VIEWED_RESULT';
export const LATEST_VIEWED_RESULT_TRACKED = 'LATEST_VIEWED_RESULT_TRACKED';
export const ERROR_TRACKING_LATEST_VIEWED_RESULT = 'ERROR_TRACKING_LATEST_VIEWED_RESULT';

export function trackLatestViewedResult(response) {
  return {
    type: LATEST_VIEWED_RESULT_TRACKED,
    latestViewedResultTracked: response
  }
}

export const handleTrackLatestViewedResult = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: TRACKING_LATEST_VIEWED_RESULT
      });

      const response = await axios.post(
        '/v1/user/users/track_result_view',
        qs.stringify(params),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(trackLatestViewedResult(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_TRACKING_LATEST_VIEWED_RESULT,
        latestViewedResults: {
          status: 'error',
          message: 'Error tracking the recent result viewed'
        }
      });
    }
  };
}

export const FETCHING_GLOBAL_SEARCH_INVESTORS = 'FETCHING_GLOBAL_SEARCH_INVESTORS';
export const GLOBAL_SEARCH_INVESTORS_FETCHED = 'GLOBAL_SEARCH_INVESTORS_FETCHED';
export const ERROR_FETCHING_GLOBAL_SEARCH_INVESTORS = 'ERROR_FETCHING_GLOBAL_SEARCH_INVESTORS';

export function fetchGlobalSearchInvestors(response) {
  return {
    type: GLOBAL_SEARCH_INVESTORS_FETCHED,
    globalSearchInvestors: response
  }
}

export const handleFetchGlobalSearchInvestors = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GLOBAL_SEARCH_INVESTORS
      });

      const response = await axios.post(
        '/v1/user/global_search/search_investors',
        qs.stringify(params),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGlobalSearchInvestors(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GLOBAL_SEARCH_INVESTORS,
        globalSearchInvestors: {
          status: 'error',
          message: 'Error fetching global search results - investors'
        }
      });
    }
  };
}

export const FETCHING_GLOBAL_SEARCH_RIA_ENTITIES = 'FETCHING_GLOBAL_SEARCH_RIA_ENTITIES';
export const GLOBAL_SEARCH_RIA_ENTITIES_FETCHED = 'GLOBAL_SEARCH_RIA_ENTITIES_FETCHED';
export const ERROR_FETCHING_GLOBAL_SEARCH_RIA_ENTITIES = 'ERROR_FETCHING_GLOBAL_SEARCH_RIA_ENTITIES';

export function fetchGlobalSearchRiaEntities(response) {
  return {
    type: GLOBAL_SEARCH_RIA_ENTITIES_FETCHED,
    globalSearchRiaEntities: response
  }
}

export const handleFetchGlobalSearchRiaEntities = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GLOBAL_SEARCH_RIA_ENTITIES
      });

      const response = await axios.post(
        '/v1/user/global_search/search_ria_entities',
        qs.stringify(params),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGlobalSearchRiaEntities(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GLOBAL_SEARCH_RIA_ENTITIES,
        globalSearchRiaEntities: {
          status: 'error',
          message: 'Error fetching global search results - ria investors'
        }
      });
    }
  };
}

export const FETCHING_GLOBAL_SEARCH_RIA_REPS = 'FETCHING_GLOBAL_SEARCH_RIA_REPS';
export const GLOBAL_SEARCH_RIA_REPS_FETCHED = 'GLOBAL_SEARCH_RIA_REPS_FETCHED';
export const ERROR_FETCHING_GLOBAL_SEARCH_RIA_REPS = 'ERROR_FETCHING_GLOBAL_SEARCH_RIA_REPS';

export function fetchGlobalSearchRiaReps(response) {
  return {
    type: GLOBAL_SEARCH_RIA_REPS_FETCHED,
    globalSearchRiaReps: response
  }
}

export const handleFetchGlobalSearchRiaReps = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GLOBAL_SEARCH_RIA_REPS
      });

      const response = await axios.post(
        '/v1/user/global_search/search_ria_reps',
        qs.stringify(params),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGlobalSearchRiaReps(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GLOBAL_SEARCH_RIA_REPS,
        globalSearchRiaReps: {
          status: 'error',
          message: 'Error fetching global search results - ria contacts'
        }
      });
    }
  };
}

export const FETCHING_GLOBAL_SEARCH_CONTACTS = 'FETCHING_GLOBAL_SEARCH_CONTACTS';
export const GLOBAL_SEARCH_CONTACTS_FETCHED = 'GLOBAL_SEARCH_CONTACTS_FETCHED';
export const ERROR_FETCHING_GLOBAL_SEARCH_CONTACTS = 'ERROR_FETCHING_GLOBAL_SEARCH_CONTACTS';

export function fetchGlobalSearchContacts(response) {
  return {
    type: GLOBAL_SEARCH_CONTACTS_FETCHED,
    globalSearchContacts: response
  }
}

export const handleFetchGlobalSearchContacts = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GLOBAL_SEARCH_CONTACTS
      });

      const response = await axios.post(
        '/v1/user/global_search/search_contacts',
        qs.stringify(params),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGlobalSearchContacts(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GLOBAL_SEARCH_CONTACTS,
        globalSearchContacts: {
          status: 'error',
          message: 'Error fetching global search results - contacts'
        }
      });
    }
  };
}

export const FETCHING_GLOBAL_SEARCH_PREV_CONTACTS = 'FETCHING_GLOBAL_SEARCH_PREV_CONTACTS';
export const GLOBAL_SEARCH_PREV_CONTACTS_FETCHED = 'GLOBAL_SEARCH_PREV_CONTACTS_FETCHED';
export const ERROR_FETCHING_GLOBAL_SEARCH_PREV_CONTACTS = 'ERROR_FETCHING_GLOBAL_SEARCH_PREV_CONTACTS';

export function fetchGlobalSearchPrevContacts(response) {
  return {
    type: GLOBAL_SEARCH_PREV_CONTACTS_FETCHED,
    globalSearchPrevContacts: response
  }
}

export const handleFetchGlobalSearchPrevContacts = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GLOBAL_SEARCH_PREV_CONTACTS
      });

      const response = await axios.post(
        '/v1/user/global_search/search_previous_contacts',
        qs.stringify(params),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGlobalSearchPrevContacts(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GLOBAL_SEARCH_PREV_CONTACTS,
        globalSearchPrevContacts: {
          status: 'error',
          message: 'Error fetching global search results - prev contacts'
        }
      });
    }
  };
}

export const FETCHING_GLOBAL_SEARCH_LISTS = 'FETCHING_GLOBAL_SEARCH_LISTS';
export const GLOBAL_SEARCH_LISTS_FETCHED = 'GLOBAL_SEARCH_LISTS_FETCHED';
export const ERROR_FETCHING_GLOBAL_SEARCH_LISTS = 'ERROR_FETCHING_GLOBAL_SEARCH_LISTS';

export function fetchGlobalSearchLists(response) {
  return {
    type: GLOBAL_SEARCH_LISTS_FETCHED,
    globalSearchLists: response
  }
}

export const handleFetchGlobalSearchLists = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GLOBAL_SEARCH_LISTS
      });

      const response = await axios.post(
        '/v1/user/global_search/search_lists',
        qs.stringify(params),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGlobalSearchLists(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GLOBAL_SEARCH_LISTS,
        globalSearchLists: {
          status: 'error',
          message: 'Error fetching global search results - lists'
        }
      });
    }
  };
}

export const FETCHING_GLOBAL_SEARCH_FUNDED_COMPANIES = 'FETCHING_GLOBAL_SEARCH_FUNDED_COMPANIES';
export const GLOBAL_SEARCH_FUNDED_COMPANIES_FETCHED = 'GLOBAL_SEARCH_FUNDED_COMPANIES_FETCHED';
export const ERROR_FETCHING_GLOBAL_SEARCH_FUNDED_COMPANIES = 'ERROR_FETCHING_GLOBAL_SEARCH_FUNDED_COMPANIES';

export function fetchGlobalSearchFundedCompanies(response) {
  return {
    type: GLOBAL_SEARCH_FUNDED_COMPANIES_FETCHED,
    globalSearchFundedCompanies: response
  }
}

export const handleFetchGlobalSearchFundedCompanies = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GLOBAL_SEARCH_FUNDED_COMPANIES
      });

      const response = await axios.post(
        '/v1/user/global_search/search_funded_companies',
        qs.stringify(params),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGlobalSearchFundedCompanies(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GLOBAL_SEARCH_FUNDED_COMPANIES,
        globalSearchFundedCompanies: {
          status: 'error',
          message: 'Error fetching global search results - funded companies'
        }
      });
    }
  };
}

export const FETCHING_GLOBAL_SEARCH_FUNDED_PROPERTIES = 'FETCHING_GLOBAL_SEARCH_FUNDED_PROPERTIES';
export const GLOBAL_SEARCH_FUNDED_PROPERTIES_FETCHED = 'GLOBAL_SEARCH_FUNDED_PROPERTIES_FETCHED';
export const ERROR_FETCHING_GLOBAL_SEARCH_FUNDED_PROPERTIES = 'ERROR_FETCHING_GLOBAL_SEARCH_FUNDED_PROPERTIES';

export function fetchGlobalSearchFundedProperties(response) {
  return {
    type: GLOBAL_SEARCH_FUNDED_PROPERTIES_FETCHED,
    globalSearchFundedProperties: response
  }
}

export const handleFetchGlobalSearchFundedProperties = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GLOBAL_SEARCH_FUNDED_PROPERTIES
      });

      const response = await axios.post(
        '/v1/user/global_search/search_funded_properties',
        qs.stringify(params),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGlobalSearchFundedProperties(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GLOBAL_SEARCH_FUNDED_PROPERTIES,
        globalSearchFundedProperties: {
          status: 'error',
          message: 'Error fetching global search results - funded properties'
        }
      });
    }
  };
}


export const FETCHING_GLOBAL_SEARCH_EF_FIRMS = 'FETCHING_GLOBAL_SEARCH_EF_FIRMS';
export const GLOBAL_SEARCH_EF_FIRMS_FETCHED = 'GLOBAL_SEARCH_EF_FIRMS_FETCHED';
export const ERROR_FETCHING_GLOBAL_SEARCH_EF_FIRMS = 'ERROR_FETCHING_GLOBAL_SEARCH_EF_FIRMS';

export function fetchGlobalSearchEfFirms(response) {
  return {
    type: GLOBAL_SEARCH_EF_FIRMS_FETCHED,
    globalSearchEfFirms: response
  }
}

export const handleFetchGlobalSearchEfFirms = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GLOBAL_SEARCH_EF_FIRMS
      });

      const response = await axios.post(
        '/v1/user/global_search/search_ef_firms',
        qs.stringify(params),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGlobalSearchEfFirms(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GLOBAL_SEARCH_EF_FIRMS,
        globalSearchEfFirms: {
          status: 'error',
          message: 'Error fetching global search results - ef firms'
        }
      });
    }
  };
}



export const FETCHING_GLOBAL_SEARCH_EF_CONTACTS = 'FETCHING_GLOBAL_SEARCH_EF_CONTACTS';
export const GLOBAL_SEARCH_EF_CONTACTS_FETCHED = 'GLOBAL_SEARCH_EF_CONTACTS_FETCHED';
export const ERROR_FETCHING_GLOBAL_SEARCH_EF_CONTACTS = 'ERROR_FETCHING_GLOBAL_SEARCH_EF_CONTACTS';

export function fetchGlobalSearchEfContacts(response) {
  return {
    type: GLOBAL_SEARCH_EF_CONTACTS_FETCHED,
    globalSearchEfContacts: response
  }
}

export const handleFetchGlobalSearchEfContacts = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GLOBAL_SEARCH_EF_CONTACTS
      });

      const response = await axios.post(
        '/v1/user/global_search/search_ef_contacts',
        qs.stringify(params),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGlobalSearchEfContacts(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GLOBAL_SEARCH_EF_CONTACTS,
        globalSearchEfContacts: {
          status: 'error',
          message: 'Error fetching global search results - ef contacts'
        }
      });
    }
  };
}


export const FETCHING_GLOBAL_SEARCH_BT_FIRMS = 'FETCHING_GLOBAL_SEARCH_BT_FIRMS';
export const GLOBAL_SEARCH_BT_FIRMS_FETCHED = 'GLOBAL_SEARCH_BT_FIRMS_FETCHED';
export const ERROR_FETCHING_GLOBAL_SEARCH_BT_FIRMS = 'ERROR_FETCHING_GLOBAL_SEARCH_BT_FIRMS';

export function fetchGlobalSearchBtFirms(response) {
  return {
    type: GLOBAL_SEARCH_BT_FIRMS_FETCHED,
    globalSearchBtFirms: response
  }
}

export const handleFetchGlobalSearchBtFirms = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GLOBAL_SEARCH_BT_FIRMS
      });

      const response = await axios.post(
          '/v1/user/global_search/search_bt_firms',
          qs.stringify(params),
          {
            cancelToken,
            headers: {
              'Content-Type': 'multipart/form-data',
              'X-User-Email': session.email,
              'X-User-Token': session.token,
              'X-User-Admin-Log-Token': session.admin_auth_token,
            },
          }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGlobalSearchBtFirms(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GLOBAL_SEARCH_BT_FIRMS,
        globalSearchBtFirms: {
          status: 'error',
          message: 'Error fetching global search results - bt firms'
        }
      });
    }
  };
}


export const FETCHING_GLOBAL_SEARCH_BT_CONTACTS = 'FETCHING_GLOBAL_SEARCH_BT_CONTACTS';
export const GLOBAL_SEARCH_BT_CONTACTS_FETCHED = 'GLOBAL_SEARCH_BT_CONTACTS_FETCHED';
export const ERROR_FETCHING_GLOBAL_SEARCH_BT_CONTACTS = 'ERROR_FETCHING_GLOBAL_SEARCH_BT_CONTACTS';

export function fetchGlobalSearchBtContacts(response) {
  return {
    type: GLOBAL_SEARCH_BT_CONTACTS_FETCHED,
    globalSearchBtContacts: response
  }
}

export const handleFetchGlobalSearchBtContacts = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GLOBAL_SEARCH_BT_CONTACTS
      });

      const response = await axios.post(
          '/v1/user/global_search/search_bt_contacts',
          qs.stringify(params),
          {
            cancelToken,
            headers: {
              'Content-Type': 'multipart/form-data',
              'X-User-Email': session.email,
              'X-User-Token': session.token,
              'X-User-Admin-Log-Token': session.admin_auth_token,
            },
          }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGlobalSearchBtContacts(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GLOBAL_SEARCH_BT_CONTACTS,
        globalSearchBtContacts: {
          status: 'error',
          message: 'Error fetching global search results - bt contacts'
        }
      });
    }
  };
}


export const FETCHING_GLOBAL_SEARCH_PRIVATE_WEALTH_TEAMS = 'FETCHING_GLOBAL_SEARCH_PRIVATE_WEALTH_TEAMS';
export const GLOBAL_SEARCH_PRIVATE_WEALTH_TEAMS_FETCHED = 'GLOBAL_SEARCH_PRIVATE_WEALTH_TEAMS_FETCHED';
export const ERROR_FETCHING_GLOBAL_SEARCH_PRIVATE_WEALTH_TEAMS = 'ERROR_FETCHING_GLOBAL_SEARCH_PRIVATE_WEALTH_TEAMS';

export function fetchGlobalSearchPrivateWealthTeams(response) {
  return {
    type: GLOBAL_SEARCH_PRIVATE_WEALTH_TEAMS_FETCHED,
    globalSearchPrivateWealthTeams: response
  }
}

export const handleFetchGlobalSearchPrivateWealthTeams = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GLOBAL_SEARCH_PRIVATE_WEALTH_TEAMS
      });

      const response = await axios.post(
        '/v1/user/global_search/search_private_wealth_teams',
        qs.stringify(params),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGlobalSearchPrivateWealthTeams(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GLOBAL_SEARCH_PRIVATE_WEALTH_TEAMS,
        globalSearchPrivateWealthTeams: {
          status: 'error',
          message: 'Error fetching global search results - wealth teams'
        }
      });
    }
  };
}

export const FETCHING_ASSOCIATED_INDUSTRIES = 'FETCHING_ASSOCIATED_INDUSTRIES';
export const ASSOCIATED_INDUSTRIES_FETCHED = 'ASSOCIATED_INDUSTRIES_FETCHED';
export const ERROR_FETCHING_ASSOCIATED_INDUSTRIES = 'ERROR_FETCHING_ASSOCIATED_INDUSTRIES';

export function fetchSectorAssociatedIndustries(response) {
  return {
    type: ASSOCIATED_INDUSTRIES_FETCHED,
    associatedIndustries: response
  }
}

export const handleFetchSectorAssociatedIndustries = (session, cancelToken, sectorIds) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_ASSOCIATED_INDUSTRIES
      });

      const response = await axios.get(
        '/v1/user/sectors/associated_industries',
        {
          params: { ids: sectorIds },
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchSectorAssociatedIndustries(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_ASSOCIATED_INDUSTRIES,
        associatedIndustries: {
          status: 'error',
          message: 'Error fetching sector associated industries'
        }
      });
    }
  };
}

export const FETCHING_TRANSACTIONS = 'FETCHING_TRANSACTIONS';
export const TRANSACTIONS_FETCHED = 'TRANSACTIONS_FETCHED';
export const ERROR_FETCHING_TRANSACTIONS = 'ERROR_FETCHING_TRANSACTIONS';

export function fetchTransactions(response) {
  return {
    type: TRANSACTIONS_FETCHED,
    transactions: response
  }
}

export const handleFetchTransactions = (session, cancelToken, params) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_TRANSACTIONS
      });

      const response = await axios.get(
        '/v1/user/transactions',
        {
          params,
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchTransactions(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_TRANSACTIONS,
        transactions: {
          status: 'error',
          message: 'Error fetching transactions'
        }
      });
    }
  };
}

export const FETCHING_HM_INV_BY_INDUSTRY = 'FETCHING_HM_INV_BY_INDUSTRY';
export const HM_INV_BY_INDUSTRY_FETCHED = 'HM_INV_BY_INDUSTRY_FETCHED';
export const ERROR_FETCHING_HM_INV_BY_INDUSTRY = 'ERROR_FETCHING_HM_INV_BY_INDUSTRY';

export function fetchHeatmapInvestmentsByIndustry(response) {
  return {
    type: HM_INV_BY_INDUSTRY_FETCHED,
    investmentsByIndustryHeatmap: response
  }
}

export const handleFetchHeatmapInvestmentsByIndustry = (session, cancelToken, params) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_HM_INV_BY_INDUSTRY
      });

      const response = await axios.get(
        '/v1/user/transactions/heatmap_investments_by_industry',
        {
          params,
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchHeatmapInvestmentsByIndustry(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_HM_INV_BY_INDUSTRY,
        investmentsByIndustryHeatmap: {
          status: 'error',
          message: 'Error fetching investments by industry for heatmap'
        }
      });
    }
  };
}

export const FETCHING_HM_INV_BY_TOTAL_AMOUNT_RAISED = 'FETCHING_HM_INV_BY_TOTAL_AMOUNT_RAISED';
export const HM_INV_BY_TOTAL_AMOUNT_RAISED_FETCHED = 'HM_INV_BY_TOTAL_AMOUNT_RAISED_FETCHED';
export const ERROR_FETCHING_HM_INV_BY_TOTAL_AMOUNT_RAISED = 'ERROR_FETCHING_HM_INV_BY_TOTAL_AMOUNT_RAISED';

export function fetchHeatmapTotalAmountRaised(response) {
  return {
    type: HM_INV_BY_TOTAL_AMOUNT_RAISED_FETCHED,
    investmentsByTotalAmountRaisedHeatmap: response
  }
}

export const handleFetchHeatmapTotalAmountRaised = (session, cancelToken, params) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_HM_INV_BY_TOTAL_AMOUNT_RAISED
      });

      const response = await axios.get(
        '/v1/user/transactions/heatmap_total_amount_raised',
        {
          params,
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchHeatmapTotalAmountRaised(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_HM_INV_BY_TOTAL_AMOUNT_RAISED,
        investmentsByTotalAmountRaisedHeatmap: {
          status: 'error',
          message: 'Error fetching investments by total amount for heatmap'
        }
      });
    }
  };
}

export const FETCHING_HM_INV_BY_STAGE_OF_INVESTMENT = 'FETCHING_HM_INV_BY_STAGE_OF_INVESTMENT';
export const HM_INV_BY_STAGE_OF_INVESTMENT_FETCHED = 'HM_INV_BY_STAGE_OF_INVESTMENT_FETCHED';
export const ERROR_FETCHING_HM_INV_BY_STAGE_OF_INVESTMENT = 'ERROR_FETCHING_HM_INV_BY_STAGE_OF_INVESTMENT';

export function fetchHeatmapStageOfInvestment(response) {
  return {
    type: HM_INV_BY_STAGE_OF_INVESTMENT_FETCHED,
    investmentsByStageOfInvestmentHeatmap: response
  }
}

export const handleFetchHeatmapStageOfInvestment = (session, cancelToken, params) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_HM_INV_BY_STAGE_OF_INVESTMENT
      });

      const response = await axios.get(
        '/v1/user/transactions/heatmap_stage_of_investment',
        {
          params,
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchHeatmapStageOfInvestment(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_HM_INV_BY_STAGE_OF_INVESTMENT,
        investmentsByStageOfInvestmentHeatmap: {
          status: 'error',
          message: 'Error fetching investments by stage of investment for heatmap'
        }
      });
    }
  };
}

export const FETCHING_HM_INV_BY_COMP_SIZE = 'FETCHING_HM_INV_BY_COMP_SIZE';
export const HM_INV_BY_COMP_SIZE_FETCHED = 'HM_INV_BY_COMP_SIZE_FETCHED';
export const ERROR_FETCHING_HM_INV_BY_COMP_SIZE = 'ERROR_FETCHING_HM_INV_BY_COMP_SIZE';

export function fetchHeatmapCompanySize(response) {
  return {
    type: HM_INV_BY_COMP_SIZE_FETCHED,
    investmentsByCompanySizeHeatmap: response
  }
}

export const handleFetchHeatmapCompanySize = (session, cancelToken, params) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_HM_INV_BY_COMP_SIZE
      });

      const response = await axios.get(
        '/v1/user/transactions/heatmap_employee_size',
        {
          params,
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchHeatmapCompanySize(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_HM_INV_BY_COMP_SIZE,
        investmentsByCompanySizeHeatmap: {
          status: 'error',
          message: 'Error fetching investments by company size for heatmap'
        }
      });
    }
  };
}

export const FETCHING_FAMILY_OFFICE_ADDITIONS = 'FETCHING_FAMILY_OFFICE_ADDITIONS';
export const FAMILY_OFFICE_ADDITIONS_FETCHED = 'FAMILY_OFFICE_ADDITIONS_FETCHED';
export const ERROR_FETCHING_FAMILY_OFFICE_ADDITIONS = 'ERROR_FETCHING_FAMILY_OFFICE_ADDITIONS';

export function fetchFamilyOfficeAdditions(response) {
  return {
    type: FAMILY_OFFICE_ADDITIONS_FETCHED,
    familyOfficeAdditions: response
  }
}

export const handleFetchFamilyOfficeAdditions = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FAMILY_OFFICE_ADDITIONS
      });

      const response = await axios.get(
        '/v1/user/dashboard/family_office_additions',
        {
          params,
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeAdditions(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FAMILY_OFFICE_ADDITIONS,
        familyOfficeAdditions: {
          status: 'error',
          message: 'Error fetching family office additions'
        }
      });
    }
  };
}

export const FETCHING_RIA_ADDITIONS = 'FETCHING_RIA_ADDITIONS';
export const RIA_ADDITIONS_FETCHED = 'RIA_ADDITIONS_FETCHED';
export const ERROR_FETCHING_RIA_ADDITIONS = 'ERROR_FETCHING_RIA_ADDITIONS';

export function fetchRiaAdditions(response) {
  return {
    type: RIA_ADDITIONS_FETCHED,
    riaAdditions: response
  }
}

export const handleFetchRiaAdditions = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_RIA_ADDITIONS
      });

      const response = await axios.get(
        '/v1/user/dashboard/ria_additions',
        {
          params,
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchRiaAdditions(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_RIA_ADDITIONS,
        riaAdditions: {
          status: 'error',
          message: 'Error fetching ria additions'
        }
      });
    }
  };
}


export const FETCHING_FAMILY_OFFICE_GROWTH = 'FETCHING_FAMILY_OFFICE_GROWTH';
export const FAMILY_OFFICE_GROWTH_FETCHED = 'FAMILY_OFFICE_GROWTH_FETCHED';
export const ERROR_FETCHING_FAMILY_OFFICE_GROWTH = 'ERROR_FETCHING_FAMILY_OFFICE_GROWTH';

export function fetchFamilyOfficeGrowth(response) {
  return {
    type: FAMILY_OFFICE_GROWTH_FETCHED,
    familyOfficeGrowth: response
  }
}

export const handleFetchFamilyOfficeGrowth = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FAMILY_OFFICE_GROWTH
      });

      const response = await axios.get(
        '/v1/user/dashboard/family_office_growth',
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeGrowth(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FAMILY_OFFICE_GROWTH,
        familyOfficeGrowth: {
          status: 'error',
          message: 'Error fetching family office growth'
        }
      });
    }
  };
}

export const FETCHING_USER_TASKS = 'FETCHING_USER_TASKS';
export const USER_TASKS_FETCHED = 'USER_TASKS_FETCHED';
export const ERROR_FETCHING_USER_TASKS = 'ERROR_FETCHING_USER_TASKS';

export function fetchTasks(response) {
  return {
    type: USER_TASKS_FETCHED,
    tasks: response
  }
}

export const handleFetchTasks = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_USER_TASKS
      });

      const response = await axios.get(
        '/v1/user/tasks',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchTasks(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_USER_TASKS,
        tasks: {
          status: 'error',
          message: 'Error fetching user Tasks'
        }
      });
    }
  };
}

export const FETCHING_DASHBOARD_FAMILY_OFFICE_LOCATIONS = 'FETCHING_DASHBOARD_FAMILY_OFFICE_LOCATIONS';
export const DASHBOARD_FAMILY_OFFICE_LOCATIONS_FETCHED = 'DASHBOARD_FAMILY_OFFICE_LOCATIONS_FETCHED';
export const ERROR_FETCHING_DASHBOARD_FAMILY_OFFICE_LOCATIONS = 'ERROR_FETCHING_DASHBOARD_FAMILY_OFFICE_LOCATIONS';

export function fetchDashboardFamilyOfficeLocations(response) {
  return {
    type: DASHBOARD_FAMILY_OFFICE_LOCATIONS_FETCHED,
    familyOfficeLocationsDashboard: response
  }
}

export const handleFetchDashboardFamilyOfficeLocations = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_DASHBOARD_FAMILY_OFFICE_LOCATIONS
      });

      const response = await axios.get(
        '/v1/user/dashboard/family_office_locations',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchDashboardFamilyOfficeLocations(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_DASHBOARD_FAMILY_OFFICE_LOCATIONS,
        familyOfficeLocationsDashboard: {
          status: 'error',
          message: 'Error fetching family office locations for dashboard'
        }
      });
    }
  };
}

export const FETCHING_LISTS_FOR_DASHBOARD_MAP = 'FETCHING_LISTS_FOR_DASHBOARD_MAP';
export const LISTS_FOR_DASHBOARD_MAP_FETCHED = 'LISTS_FOR_DASHBOARD_MAP_FETCHED';
export const ERROR_FETCHING_LISTS_FOR_DASHBOARD_MAP = 'ERROR_FETCHING_LISTS_FOR_DASHBOARD_MAP';

export function fetchDashboardListsForMap(response) {
  return {
    type: LISTS_FOR_DASHBOARD_MAP_FETCHED,
    familyOfficeListsForMap: response
  }
}

export const handleFetchDashboardListsForMap = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_LISTS_FOR_DASHBOARD_MAP
      });

      const response = await axios.get(
        '/v1/user/lists/for_dashboard_map',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchDashboardListsForMap(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_LISTS_FOR_DASHBOARD_MAP,
        familyOfficeListsForMap: {
          status: 'error',
          message: 'Error fetching lists for dashboard map'
        }
      });
    }
  };
}

export const SAVE_SELECTED_LISTS_DASHBOARD_MAP = 'SAVE_SELECTED_LISTS_DASHBOARD_MAP';

export function saveSelectedListsDashboardMap(selectedListsDashboardMap) {
  return {
    type: SAVE_SELECTED_LISTS_DASHBOARD_MAP,
    selectedListsDashboardMap,
  }
}

export const handleSelectedListsDashboardMap = (selectedListsDashboardMap) => {
  return dispatch => {
    dispatch(saveSelectedListsDashboardMap(selectedListsDashboardMap))
  }
}


export const FETCHING_ACTIVITY_REPORTS_NOTES = 'FETCHING_ACTIVITY_REPORTS_NOTES';
export const ACTIVITY_REPORTS_NOTES_FETCHED = 'ACTIVITY_REPORTS_NOTES_FETCHED';
export const ERROR_FETCHING_ACTIVITY_REPORTS_NOTES = 'ERROR_FETCHING_ACTIVITY_REPORTS_NOTES';

export function fetchActivityReportsNotes(response) {
  return {
    type: ACTIVITY_REPORTS_NOTES_FETCHED,
    activityReportsNotes: response
  }
}

export const handleFetchActivityReportsNotes = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_ACTIVITY_REPORTS_NOTES
      });

      const response = await axios.get(
        '/v1/user/activity_reports/notes',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchActivityReportsNotes(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_ACTIVITY_REPORTS_NOTES,
        activityReportsNotes: {
          status: 'error',
          message: 'Error fetching Activity Report Notes'
        }
      });
    }
  };
}

export const FETCHING_ACTIVITY_REPORTS_TASKS = 'FETCHING_ACTIVITY_REPORTS_TASKS';
export const ACTIVITY_REPORTS_TASKS_FETCHED = 'ACTIVITY_REPORTS_TASKS_FETCHED';
export const ERROR_FETCHING_ACTIVITY_REPORTS_TASKS = 'ERROR_FETCHING_ACTIVITY_REPORTS_TASKS';

export function fetchActivityReportsTasks(response) {
  return {
    type: ACTIVITY_REPORTS_TASKS_FETCHED,
    activityReportsTasks: response
  }
}

export const handleFetchActivityReportsTasks = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_ACTIVITY_REPORTS_TASKS
      });

      const response = await axios.get(
        '/v1/user/activity_reports/tasks',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchActivityReportsTasks(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_ACTIVITY_REPORTS_TASKS,
        activityReportsTasks: {
          status: 'error',
          message: 'Error fetching Activity Report Notes'
        }
      });
    }
  };
}

export const FETCHING_ACCESSIBLE_LISTS = 'FETCHING_ACCESSIBLE_LISTS';
export const ACCESSIBLE_LISTS_FETCHED = 'ACCESSIBLE_LISTS_FETCHED';
export const ERROR_FETCHING_ACCESSIBLE_LISTS = 'ERROR_FETCHING_ACCESSIBLE_LISTS';

export function fetchUserAccessibleLists(response) {
  return {
    type: ACCESSIBLE_LISTS_FETCHED,
    userAccessibleLists: response
  }
}

export const handleFetchUserAccessibleLists = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_ACCESSIBLE_LISTS
      });

      const response = await axios.get(
        '/v1/user/lists',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchUserAccessibleLists(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_ACCESSIBLE_LISTS,
        userAccessibleLists: {
          status: 'error',
          message: 'Error fetching accessible lists'
        }
      });
    }
  };
}

export const FETCHING_ACCESSIBLE_LISTS_FOLDERS = 'FETCHING_ACCESSIBLE_LISTS_FOLDERS';
export const ACCESSIBLE_LISTS_FOLDERS_FETCHED = 'ACCESSIBLE_LISTS_FOLDERS_FETCHED';
export const ERROR_FETCHING_ACCESSIBLE_LISTS_FOLDERS = 'ERROR_FETCHING_ACCESSIBLE_LISTS_FOLDERS';

export function fetchUserAccessibleListsFolders(response) {
  return {
    type: ACCESSIBLE_LISTS_FOLDERS_FETCHED,
    userAccessibleListsFolders: response
  }
}

export const handleFetchUserAccessibleListsFolders = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_ACCESSIBLE_LISTS_FOLDERS
      });

      const response = await axios.get(
        '/v1/user/lists_folders',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchUserAccessibleListsFolders(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_ACCESSIBLE_LISTS_FOLDERS,
        userAccessibleListsFolders: {
          status: 'error',
          message: 'Error fetching accessible lists folders'
        }
      });
    }
  };
}

export const CREATING_LISTS_FOLDER = 'CREATING_LISTS_FOLDER';
export const LISTS_FOLDER_CREATED = 'LISTS_FOLDER_CREATED';
export const ERROR_CREATING_LISTS_FOLDER = 'ERROR_CREATING_LISTS_FOLDER';

export function createListsFolder(response) {
  return {
    type: LISTS_FOLDER_CREATED,
    listsFolder: response
  }
}

export const handleCreateListsFolder = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: CREATING_LISTS_FOLDER
      });

      const response = await axios.post(
        '/v1/user/lists_folders',
        qs.stringify({ lists_folder: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(createListsFolder(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_CREATING_LISTS_FOLDER,
        listsFolder: {
          status: 'error',
          message: 'Error creating lists folder'
        }
      });
    }
  };
}

export const FETCHING_NUMBER_STATISTICS = 'FETCHING_NUMBER_STATISTICS';
export const NUMBER_STATISTICS_FETCHED = 'NUMBER_STATISTICS_FETCHED';
export const ERROR_FETCHING_NUMBER_STATISTICS = 'ERROR_FETCHING_NUMBER_STATISTICS';

export function fetchDashboardNumberStatistics(response) {
  return {
    type: NUMBER_STATISTICS_FETCHED,
    numberStatistics: response
  }
}

export const handleFetchDashboardNumberStatistics = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_NUMBER_STATISTICS
      });

      const response = await axios.get(
        '/v1/user/dashboard/number_statistics',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchDashboardNumberStatistics(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_NUMBER_STATISTICS,
        numberStatistics: {
          status: 'error',
          message: 'Error fetching dashboard number statistics'
        }
      });
    }
  };
}

export const FETCHING_SHARED_LISTS_OWNERS = 'FETCHING_SHARED_LISTS_OWNERS';
export const SHARED_LISTS_OWNERS_FETCHED = 'SHARED_LISTS_OWNERS_FETCHED';
export const ERROR_FETCHING_SHARED_LISTS_OWNERS = 'ERROR_FETCHING_SHARED_LISTS_OWNERS';

export function fetchSharedListsOwners(response) {
  return {
    type: SHARED_LISTS_OWNERS_FETCHED,
    sharedListsOwners: response
  }
}

export const handleFetchSharedListsOwners = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_SHARED_LISTS_OWNERS
      });

      const response = await axios.get(
        '/v1/user/lists/shared_lists_owners',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchSharedListsOwners(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_SHARED_LISTS_OWNERS,
        sharedListsOwners: {
          status: 'error',
          message: 'Error fetching shared lists owners'
        }
      });
    }
  };
}

export const FETCHING_SHARED_LISTS_TYPES = 'FETCHING_SHARED_LISTS_TYPES';
export const SHARED_LISTS_TYPES_FETCHED = 'SHARED_LISTS_TYPES_FETCHED';
export const ERROR_FETCHING_SHARED_LISTS_TYPES = 'ERROR_FETCHING_SHARED_LISTS_TYPES';

export function fetchSharedListsTypes(response) {
  return {
    type: SHARED_LISTS_TYPES_FETCHED,
    sharedListsTypes: response
  }
}

export const handleFetchSharedListsTypes = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_SHARED_LISTS_TYPES
      });

      const response = await axios.get(
        '/v1/user/lists/shared_lists_types',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchSharedListsTypes(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_SHARED_LISTS_TYPES,
        sharedListsTypes: {
          status: 'error',
          message: 'Error fetching shared lists types'
        }
      });
    }
  };
}

export const SAVE_SELECTED_LISTS_FILTERS = 'SAVE_SELECTED_LISTS_FILTERS';

export function saveSelectedListsFilters(selectedListsFilters) {
  return {
    type: SAVE_SELECTED_LISTS_FILTERS,
    selectedListsFilters,
  }
}

export const handleSelectedListsFilters = (selectedListsFilters) => {
  return dispatch => {
    dispatch(saveSelectedListsFilters(selectedListsFilters))
  }
}

export const FETCHING_FO_BY_REGION = 'FETCHING_FO_BY_REGION';
export const FO_BY_REGION_FETCHED = 'FO_BY_REGION_FETCHED';
export const ERROR_FETCHING_FO_BY_REGION = 'ERROR_FETCHING_FO_BY_REGION';

export function fetchFamilyOfficeByRegions(response) {
  return {
    type: FO_BY_REGION_FETCHED,
    familyOfficesByRegion: response
  }
}

export const handleFetchFamilyOfficeByRegions = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FO_BY_REGION
      });

      const response = await axios.get(
        '/v1/user/dashboard/family_office_by_regions',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeByRegions(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FO_BY_REGION,
        familyOfficesByRegion: {
          status: 'error',
          message: 'Error fetching family offices by region'
        }
      });
    }
  };
}

export const FETCHING_SHARED_LISTS_FOLDERS_OWNERS = 'FETCHING_SHARED_LISTS_FOLDERS_OWNERS';
export const SHARED_LISTS_FOLDERS_OWNERS_FETCHED = 'SHARED_LISTS_FOLDERS_OWNERS_FETCHED';
export const ERROR_FETCHING_SHARED_LISTS_FOLDERS_OWNERS = 'ERROR_FETCHING_SHARED_LISTS_FOLDERS_OWNERS';

export function fetchSharedListsFoldersOwners(response) {
  return {
    type: SHARED_LISTS_FOLDERS_OWNERS_FETCHED,
    sharedListsFoldersOwners: response
  }
}

export const handleFetchSharedListsFoldersOwners = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_SHARED_LISTS_FOLDERS_OWNERS
      });

      const response = await axios.get(
        '/v1/user/lists_folders/shared_lists_folders_owners',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchSharedListsFoldersOwners(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_SHARED_LISTS_FOLDERS_OWNERS,
        sharedListsFoldersOwners: {
          status: 'error',
          message: 'Error fetching shared lists folders owners'
        }
      });
    }
  };
}

export const SAVE_SELECTED_LISTS_FOLDERS_FILTERS = 'SAVE_SELECTED_LISTS_FOLDERS_FILTERS';

export function saveSelectedListsFoldersFilters(selectedListsFoldersFilters) {
  return {
    type: SAVE_SELECTED_LISTS_FOLDERS_FILTERS,
    selectedListsFoldersFilters,
  }
}

export const handleSelectedListsFoldersFilters = (selectedListsFoldersFilters) => {
  return dispatch => {
    dispatch(saveSelectedListsFoldersFilters(selectedListsFoldersFilters))
  }
}

export const SAVING_NOTE_PARAMS = 'SAVING_NOTE_PARAMS';
export const NOTE_PARAMS_SAVED = 'NOTE_PARAMS_SAVED';
export const ERROR_SAVING_NOTE_PARAMS = 'ERROR_SAVING_NOTE_PARAMS';

export function saveNoteParams(response) {
  return {
    type: NOTE_PARAMS_SAVED,
    noteParamsSaved: response
  }
}

export const handleSaveNoteParams = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: SAVING_NOTE_PARAMS
      });

      const response = await axios.post(
        `/v1/user/notes`,
        qs.stringify({ note: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(saveNoteParams(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_SAVING_NOTE_PARAMS,
        noteParamsSaved: {
          status: 'error',
          message: 'Error saving Note params'
        }
      });
    }
  };
}

export const UPDATING_NOTE_PARAMS = 'UPDATING_NOTE_PARAMS';
export const NOTE_PARAMS_UPDATED = 'NOTE_PARAMS_UPDATED';
export const ERROR_UPDATING_NOTE_PARAMS = 'ERROR_UPDATING_NOTE_PARAMS';

export function updateNotesParams(response) {
  return {
    type: NOTE_PARAMS_UPDATED,
    noteParamsUpdated: response
  }
}

export const handleUpdateNoteParams = (session, cancelToken, noteId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: UPDATING_NOTE_PARAMS
      });

      const response = await axios.put(
        `/v1/user/notes/${noteId}`,
        qs.stringify({ note: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(updateNotesParams(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_UPDATING_NOTE_PARAMS,
        noteParamsUpdated: {
          status: 'error',
          message: 'Error updating Note params'
        }
      });
    }
  };
}

export const SAVING_TASK_PARAMS = 'SAVING_TASK_PARAMS';
export const TASK_PARAMS_SAVED = 'TASK_PARAMS_SAVED';
export const ERROR_SAVING_TASK_PARAMS = 'ERROR_SAVING_TASK_PARAMS';

export function saveTaskParams(response) {
  return {
    type: TASK_PARAMS_SAVED,
    taskParamsSaved: response
  }
}

export const handleSaveTaskParams = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: SAVING_TASK_PARAMS
      });

      const response = await axios.post(
        `/v1/user/tasks`,
        qs.stringify({ task: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(saveTaskParams(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_SAVING_TASK_PARAMS,
        taskParamsSaved: {
          status: 'error',
          message: 'Error saving Task params'
        }
      });
    }
  };
}

export const UPDATING_TASK_PARAMS = 'UPDATING_TASK_PARAMS';
export const TASK_PARAMS_UPDATED = 'TASK_PARAMS_UPDATED';
export const ERROR_UPDATING_TASK_PARAMS = 'ERROR_UPDATING_TASK_PARAMS';

export function updateTasksParams(response) {
  return {
    type: TASK_PARAMS_UPDATED,
    taskParamsUpdated: response
  }
}

export const handleUpdateTaskParams = (session, cancelToken, taskId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: UPDATING_TASK_PARAMS
      });

      const response = await axios.put(
        `/v1/user/tasks/${taskId}`,
        qs.stringify({ task: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(updateTasksParams(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_UPDATING_TASK_PARAMS,
        taskParamsUpdated: {
          status: 'error',
          message: 'Error updating Task params'
        }
      });
    }
  };
}

export const FETCHING_LIST_TYPES = 'FETCHING_LIST_TYPES';
export const LIST_TYPES_FETCHED = 'LIST_TYPES_FETCHED';
export const ERROR_FETCHING_LIST_TYPES = 'ERROR_FETCHING_LIST_TYPES';

export function fetchListTypes(response) {
  return {
    type: LIST_TYPES_FETCHED,
    listTypes: response
  }
}

export const handleFetchListTypes = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_LIST_TYPES
      });
      //hack to avoid calling multiple times to note types controller
      const hardcodedDataResponse = { status: 'fetched', note_types: session.note_types }
      dispatch(fetchNoteTypes(hardcodedDataResponse));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_LIST_TYPES,
        listTypes: {
          status: 'error',
          message: 'Error fetching list types'
        }
      });
    }
  };
}

export const FETCHING_LISTS_FOLDERS = 'FETCHING_LISTS_FOLDERS';
export const LISTS_FOLDERS_FETCHED = 'LISTS_FOLDERS_FETCHED';
export const ERROR_FETCHING_LISTS_FOLDERS = 'ERROR_FETCHING_LISTS_FOLDERS';

export function fetchListsFolders(response) {
  return {
    type: LISTS_FOLDERS_FETCHED,
    listsFolders: response
  }
}

export const handleFetchListsFolders = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_LISTS_FOLDERS
      });

      const response = await axios.get(
        '/v1/user/lists_folders/user_lists_folders',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchListsFolders(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_LISTS_FOLDERS,
        listsFolders: {
          status: 'error',
          message: 'Error fetching lists folders'
        }
      });
    }
  };
}

export const SAVING_FEEDBACK_PARAMS = 'SAVING_FEEDBACK_PARAMS';
export const FEEDBACK_PARAMS_SAVED = 'FEEDBACK_PARAMS_SAVED';
export const ERROR_SAVING_FEEDBACK_PARAMS = 'ERROR_SAVING_FEEDBACK_PARAMS';

export function saveFeedbackParams(response) {
  return {
    type: FEEDBACK_PARAMS_SAVED,
    feedbackParamsSaved: response
  }
}

export const handleSaveFeedbackParams = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: SAVING_FEEDBACK_PARAMS
      });

      const response = await axios.post(
        `/v1/user/feedbacks`,
        qs.stringify({ feedback: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(saveFeedbackParams(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_SAVING_FEEDBACK_PARAMS,
        feedbackParamsSaved: {
          status: 'error',
          message: 'Error saving feedback params'
        }
      });
    }
  };
}

export const FETCHING_RECENTLY_VIEWED_ITEMS = 'FETCHING_RECENTLY_VIEWED_ITEMS';
export const RECENTLY_VIEWED_ITEMS_FETCHED = 'RECENTLY_VIEWED_ITEMS_FETCHED';
export const ERROR_FETCHING_RECENTLY_VIEWED_ITEMS = 'ERROR_FETCHING_RECENTLY_VIEWED_ITEMS';

export function fetchRecentlyViewedItems(response) {
  return {
    type: RECENTLY_VIEWED_ITEMS_FETCHED,
    recentlyViewedItems: response
  }
}

export const handleFetchRecentlyViewedItems = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_RECENTLY_VIEWED_ITEMS
      });

      const response = await axios.get(
        '/v1/user/dashboard/recently_viewed',
        {
          params,
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchRecentlyViewedItems(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_RECENTLY_VIEWED_ITEMS,
        recentlyViewedItems: {
          status: 'error',
          message: 'Error fetching recently viewed items'
        }
      });
    }
  };
}

export const FETCHING_AFFINITY_MATCH_ITEMS = 'FETCHING_AFFINITY_MATCH_ITEMS';
export const AFFINITY_MATCH_ITEMS_FETCHED = 'AFFINITY_MATCH_ITEMS_FETCHED';
export const ERROR_FETCHING_AFFINITY_MATCH_ITEMS = 'ERROR_FETCHING_AFFINITY_MATCH_ITEMS';

export function fetchAffinityMatchItems(response) {
  return {
    type: AFFINITY_MATCH_ITEMS_FETCHED,
    affinityMatchItems: response
  }
}

export const handleFetchAffinityMatchedItems = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_AFFINITY_MATCH_ITEMS
      });

      const response = await axios.get(
        '/v1/user/dashboard/affinity_matches',
        {
          params,
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchAffinityMatchItems(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_AFFINITY_MATCH_ITEMS,
        affinityMatchItems: {
          status: 'error',
          message: 'Error fetching recently viewed items'
        }
      });
    }
  };
}

export const FETCHING_INDUSTRY_INVESTMENTS = 'FETCHING_INDUSTRY_INVESTMENTS';
export const INDUSTRY_INVESTMENTS_FETCHED = 'INDUSTRY_INVESTMENTS_FETCHED';
export const ERROR_FETCHING_INDUSTRY_INVESTMENTS = 'ERROR_FETCHING_INDUSTRY_INVESTMENTS';

export function fetchIndustryInvestments(response) {
  return {
    type: INDUSTRY_INVESTMENTS_FETCHED,
    industryInvestments: response
  }
}

export const handleFetchIndustryInvestments = (session, cancelToken, industryId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_INDUSTRY_INVESTMENTS
      });

      const response = await axios.get(
        `/v1/user/industries/${industryId}`,
        {
          params,
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchIndustryInvestments(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_INDUSTRY_INVESTMENTS,
        industryInvestments: {
          status: 'error',
          message: 'Error fetching industry Investments'
        }
      });
    }
  };
}

export const FETCHING_INDUSTRY_NEWS = 'FETCHING_INDUSTRY_NEWS';
export const INDUSTRY_NEWS_FETCHED = 'INDUSTRY_NEWS_FETCHED';
export const ERROR_FETCHING_INDUSTRY_NEWS = 'ERROR_FETCHING_INDUSTRY_NEWS';

export function fetchIndustryNews(response) {
  return {
    type: INDUSTRY_NEWS_FETCHED,
    industryNews: response
  }
}

export const handleFetchIndustryNews = (session, cancelToken, industryId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_INDUSTRY_NEWS
      });

      const response = await axios.get(
        `/v1/user/industries/${industryId}/news`,
        {
          params,
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchIndustryNews(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_INDUSTRY_NEWS,
        industryNews: {
          status: 'error',
          message: 'Error fetching industry News'
        }
      });
    }
  };
}

export const UPDATING_LISTS_FOLDER = 'UPDATING_LISTS_FOLDER';
export const LISTS_FOLDER_UPDATED = 'LISTS_FOLDER_UPDATED';
export const ERROR_UPDATING_LISTS_FOLDER = 'ERROR_UPDATING_LISTS_FOLDER';

export function updateListsFolder(response) {
  return {
    type: LISTS_FOLDER_UPDATED,
    listsFolder: response
  }
}

export const handleUpdateListsFolder = (session, cancelToken, folderId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: UPDATING_LISTS_FOLDER
      });

      const response = await axios.put(
        `/v1/user/lists_folders/${folderId}`,
        qs.stringify({ lists_folder: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(updateListsFolder(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_UPDATING_LISTS_FOLDER,
        listsFolder: {
          status: 'error',
          message: 'Error updating lists folder'
        }
      });
    }
  };
}

export const CLONING_LIST = 'CLONING_LIST';
export const LIST_CLONED = 'LIST_CLONED';
export const ERROR_CLONING_LIST = 'ERROR_CLONING_LIST';

export function cloneList(response) {
  return {
    type: LIST_CLONED,
    listCloned: response
  }
}

export const handleCloneList = (session, cancelToken, listId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: CLONING_LIST
      });

      const response = await axios.post(
        `/v1/user/lists/${listId}/clone`,
        qs.stringify({ list: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(cloneList(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_CLONING_LIST,
        listCloned: {
          status: 'error',
          message: 'Error cloning list'
        }
      });
    }
  };
}

export const ARCHIVING_LIST = 'ARCHIVING_LIST';
export const LIST_ARCHIVED = 'LIST_ARCHIVED';
export const ERROR_ARCHIVING_LIST = 'ERROR_ARCHIVING_LIST';

export function archiveList(response) {
  return {
    type: LIST_ARCHIVED,
    listArchived: response
  }
}

export const handleArchiveList = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: ARCHIVING_LIST
      });

      const response = await axios.put(
        `/v1/user/lists/archive`,
        qs.stringify({ list: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(archiveList(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_ARCHIVING_LIST,
        listArchived: {
          status: 'error',
          message: 'Error archiving lists'
        }
      });
    }
  };
}

export const ARCHIVING_LISTS_FOLDER = 'ARCHIVING_LISTS_FOLDER';
export const LISTS_FOLDER_ARCHIVED = 'LISTS_FOLDER_ARCHIVED';
export const ERROR_ARCHIVING_LISTS_FOLDER = 'ERROR_ARCHIVING_LISTS_FOLDER';

export function archiveListsFolder(response) {
  return {
    type: LISTS_FOLDER_ARCHIVED,
    listsFolderArchived: response
  }
}

export const handleArchiveListsFolder = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: ARCHIVING_LISTS_FOLDER
      });

      const response = await axios.put(
        `/v1/user/lists_folders/archive`,
        qs.stringify({ lists_folder: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(archiveListsFolder(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_ARCHIVING_LISTS_FOLDER,
        listsFolderArchived: {
          status: 'error',
          message: 'Error archiving lists folder'
        }
      });
    }
  };
}

export const FETCHING_ACCESSIBLE_LISTS_FOLDERS_ADD_TO_LIST = 'FETCHING_ACCESSIBLE_LISTS_FOLDERS_ADD_TO_LIST';
export const ACCESSIBLE_LISTS_FOLDERS_ADD_TO_LIST_FETCHED = 'ACCESSIBLE_LISTS_FOLDERS_ADD_TO_LIST_FETCHED';
export const ERROR_FETCHING_ACCESSIBLE_LISTS_FOLDERS_ADD_TO_LIST = 'ERROR_FETCHING_ACCESSIBLE_LISTS_FOLDERS_ADD_TO_LIST';

export function fetchUserAccessibleListsFoldersForAddToList(response) {
  return {
    type: ACCESSIBLE_LISTS_FOLDERS_ADD_TO_LIST_FETCHED,
    userAccessibleListsFoldersForAddToList: response
  }
}

export const handleFetchUserAccessibleListsFoldersForAddToList = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_ACCESSIBLE_LISTS_FOLDERS_ADD_TO_LIST
      });

      const response = await axios.get(
        '/v1/user/lists_folders',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchUserAccessibleListsFoldersForAddToList(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_ACCESSIBLE_LISTS_FOLDERS_ADD_TO_LIST,
        userAccessibleListsFoldersForAddToList: {
          status: 'error',
          message: 'Error fetching accessible lists folders'
        }
      });
    }
  };
}

export const FETCHING_TEAMMATES_TO_SHARE_WITH = 'FETCHING_TEAMMATES_TO_SHARE_WITH';
export const TEAMMATES_TO_SHARE_WITH_FETCHED = 'TEAMMATES_TO_SHARE_WITH_FETCHED';
export const ERROR_FETCHING_TEAMMATES_TO_SHARE_WITH = 'ERROR_FETCHING_TEAMMATES_TO_SHARE_WITH';

export function fetchTeammatesToShareWith(response) {
  return {
    type: TEAMMATES_TO_SHARE_WITH_FETCHED,
    teammatesToShareWith: response
  }
}

export const handleFetchTeammatesToShareWith = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_TEAMMATES_TO_SHARE_WITH
      });

      const response = await axios.get(
        '/v1/user/teammates/share_with',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchTeammatesToShareWith(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_TEAMMATES_TO_SHARE_WITH,
        teammatesToShareWith: {
          status: 'error',
          message: 'Error fetching teammates'
        }
      });
    }
  };
}

export const SHARING_ITEM = 'SHARING_ITEM';
export const ITEM_SHARED = 'ITEM_SHARED';
export const ERROR_SHARING_ITEM = 'ERROR_SHARING_ITEM';

export function shareItemWithTeammates(response) {
  return {
    type: ITEM_SHARED,
    itemShared: response
  }
}

export const handleShareItemWithTeammates = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: SHARING_ITEM
      });

      const response = await axios.post(
        `/v1/user/teammates/share_item`,
        qs.stringify({ teammate: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(shareItemWithTeammates(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_SHARING_ITEM,
        itemShared: {
          status: 'error',
          message: 'Error sharing item'
        }
      });
    }
  };
}

export const FETCHING_CONTACTS_ASSOCIATED_WITH_LIST = 'FETCHING_CONTACTS_ASSOCIATED_WITH_LIST';
export const CONTACTS_ASSOCIATED_WITH_LIST_FETCHED = 'CONTACTS_ASSOCIATED_WITH_LIST_FETCHED';
export const ERROR_FETCHING_CONTACTS_ASSOCIATED_WITH_LIST = 'ERROR_FETCHING_CONTACTS_ASSOCIATED_WITH_LIST';

export function fetchAssociatedContactsWithList(response) {
  return {
    type: CONTACTS_ASSOCIATED_WITH_LIST_FETCHED,
    associatedWithListContacts: response
  }
}

let cancelFetchContactsResultsToken;
export const handleFetchAssociatedContactsWithList = (session, cancelToken, listId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_CONTACTS_ASSOCIATED_WITH_LIST
      });

      if (typeof cancelFetchContactsResultsToken != typeof undefined) {
        cancelFetchContactsResultsToken.cancel("Operation canceled due to new request. contacts");
      }

      cancelFetchContactsResultsToken = axios.CancelToken.source();

        const p = {
            ...params,
            ...(params.search_filter && {
                search_filter: JSON.stringify(params.search_filter)
            }),
            ...(params.sort_builder && {
                sort_builder: JSON.stringify(params.sort_builder)
            }),
        };


        const response = await axios.post(
        `/v1/user/lists/${listId}/contacts2`,
          qs.stringify(p, { arrayFormat: 'brackets' }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken: cancelFetchContactsResultsToken.token,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchAssociatedContactsWithList(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_CONTACTS_ASSOCIATED_WITH_LIST,
        associatedWithListContacts: {
          status: 'error',
          message: 'Error fetching contacts'
        }
      });
    }
  };
}

export const FETCHING_CONTACTS_FOR_ADD_TO_LIST = 'FETCHING_CONTACTS_FOR_ADD_TO_LIST';
export const CONTACTS_FOR_ADD_TO_LIST_FETCHED = 'CONTACTS_FOR_ADD_TO_LIST_FETCHED';
export const ERROR_FETCHING_CONTACTS_FOR_ADD_TO_LIST = 'ERROR_FETCHING_CONTACTS_FOR_ADD_TO_LIST';

export function fetchNotInListContacts(response) {
  return {
    type: CONTACTS_FOR_ADD_TO_LIST_FETCHED,
    userContactsForAddToList: response
  }
}

export const handleFetchNotInListContacts = (session, cancelToken, listId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_CONTACTS_FOR_ADD_TO_LIST
      });

      const response = await axios.get(
        `/v1/user/lists/${listId}/contacts_not_in_list`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchNotInListContacts(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_CONTACTS_FOR_ADD_TO_LIST,
        userContactsForAddToList: {
          status: 'error',
          message: 'Error fetching contacts for add to list'
        }
      });
    }
  };
}

export const FETCHING_INVESTORS_FOR_ADD_TO_LIST = 'FETCHING_INVESTORS_FOR_ADD_TO_LIST';
export const INVESTORS_FOR_ADD_TO_LIST_FETCHED = 'INVESTORS_FOR_ADD_TO_LIST_FETCHED';
export const ERROR_FETCHING_INVESTORS_FOR_ADD_TO_LIST = 'ERROR_FETCHING_INVESTORS_FOR_ADD_TO_LIST';

export function fetchNotInListInvestors(response) {
  return {
    type: INVESTORS_FOR_ADD_TO_LIST_FETCHED,
    userInvestorsForAddToList: response
  }
}

export const handleFetchNotInListInvestors = (session, cancelToken, listId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_INVESTORS_FOR_ADD_TO_LIST
      });

      const response = await axios.get(
        `/v1/user/lists/${listId}/investors_not_in_list`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchNotInListInvestors(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_INVESTORS_FOR_ADD_TO_LIST,
        userInvestorsForAddToList: {
          status: 'error',
          message: 'Error fetching investors for add to list'
        }
      });
    }
  };
}

export const ADDING_ITEM_TO_LIST = 'ADDING_ITEM_TO_LIST';
export const ITEM_ADDED_TO_LIST = 'ITEM_ADDED_TO_LIST';
export const ERROR_ADDING_ITEM_TO_LIST = 'ERROR_ADDING_ITEM_TO_LIST';

export function addSelectedItemsToList(response) {
  return {
    type: ITEM_ADDED_TO_LIST,
    addedSelectedItems: response
  }
}

export const handleAddSelectedItemsToList = (session, cancelToken, listId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: ADDING_ITEM_TO_LIST
      });

      const response = await axios.post(
        `/v1/user/lists/${listId}/add_item`,
        qs.stringify({ add_to_list: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(addSelectedItemsToList(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_ADDING_ITEM_TO_LIST,
        addedSelectedItems: {
          status: 'error',
          message: 'Error adding selected items'
        }
      });
    }
  };
}

export const FETCHING_FEATURE_UPGRADES = 'FETCHING_FEATURE_UPGRADES';
export const FEATURE_UPGRADES_FETCHED = 'FEATURE_UPGRADES_FETCHED';
export const ERROR_FETCHING_FEATURE_UPGRADES = 'ERROR_FETCHING_FEATURE_UPGRADES';

export function fetchFeatureUpgrades(response) {
  return {
    type: FEATURE_UPGRADES_FETCHED,
    featureUpgrades: response
  }
}

export const handleFetchFeatureUpgrades = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FEATURE_UPGRADES
      });

      const response = await axios.get(
        '/v1/user/feature_upgrades',
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });
      dispatch(fetchFeatureUpgrades(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FEATURE_UPGRADES,
        featureUpgrades: {
          status: 'error',
          message: 'Error fetching Feature Upgrades'
        }
      });
    }
  };
}

export const FETCHING_FEATURE_UPGRADE = 'FETCHING_FEATURE_UPGRADE';
export const FEATURE_UPGRADE_FETCHED = 'FEATURE_UPGRADE_FETCHED';
export const ERROR_FETCHING_FEATURE_UPGRADE = 'ERROR_FETCHING_FEATURE_UPGRADE';

export function fetchFeatureUpgrade(response) {
  return {
    type: FEATURE_UPGRADE_FETCHED,
    featureUpgrade: response
  }
}
export const handleFetchFeatureUpgrade = (session, cancelToken, featureId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FEATURE_UPGRADE
      });

      const response = await axios.get(
        `/v1/user/feature_upgrades/${featureId}`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFeatureUpgrade(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FEATURE_UPGRADE,
        featureUpgrade: {
          status: 'error',
          message: 'Error fetching Feature Upgrade'
        }
      });
    }
  };
}

export const SENDING_FEATURE_UPGRADE_EMAIL = 'SENDING_FEATURE_UPGRADE_EMAIL';
export const FEATURE_UPGRADE_EMAIL_SENT = 'FEATURE_UPGRADE_EMAIL_SENT';
export const ERROR_SENDING_FEATURE_UPGRADE_EMAIL = 'ERROR_SENDING_FEATURE_UPGRADE_EMAIL';

export function sendFeatureUpgradeEmail(response) {
  return {
    type: FEATURE_UPGRADE_EMAIL_SENT,
    featureUpgradeEmailSent: response
  }
}

export const handleFeatureUpgradeEmail = (session, cancelToken, featureId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: SENDING_FEATURE_UPGRADE_EMAIL
      });

      const response = await axios.post(
        `/v1/user/feature_upgrades/${featureId}/send_email`,
        qs.stringify({ email: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(sendFeatureUpgradeEmail(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_SENDING_FEATURE_UPGRADE_EMAIL,
        featureUpgradeEmailSent: {
          status: 'error',
          message: 'Error sending Feature Upgrade email'
        }
      });
    }
  };
}

export const CREATING_NOTES_FOR_LIST_RECORDS = 'CREATING_NOTES_FOR_LIST_RECORDS';
export const NOTES_FOR_LIST_RECORDS_CREATED = 'NOTES_FOR_LIST_RECORDS_CREATED';
export const ERROR_CREATING_NOTES_FOR_LIST_RECORDS = 'ERROR_CREATING_NOTES_FOR_LIST_RECORDS';

export function createNotesForListRecords(response) {
  return {
    type: NOTES_FOR_LIST_RECORDS_CREATED,
    userCreatedNotes: response
  }
}

export const handleCreateNotesForListRecords = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: CREATING_NOTES_FOR_LIST_RECORDS
      });

      const response = await axios.post(
        '/v1/user/notes/create_for_list_records',
        qs.stringify({ note: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(createNotesForListRecords(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_CREATING_NOTES_FOR_LIST_RECORDS,
        userCreatedNotes: {
          status: 'error',
          message: 'Error creating notes for selected records'
        }
      });
    }
  };
}

export const CREATING_TASKS_FOR_LIST_RECORDS = 'CREATING_TASKS_FOR_LIST_RECORDS';
export const TASKS_FOR_LIST_RECORDS_CREATED = 'TASKS_FOR_LIST_RECORDS_CREATED';
export const ERROR_CREATING_TASKS_FOR_LIST_RECORDS = 'ERROR_CREATING_TASKS_FOR_LIST_RECORDS';

export function createTasksForListRecords(response) {
  return {
    type: TASKS_FOR_LIST_RECORDS_CREATED,
    userCreatedTasks: response
  }
}

export const handleCreateTasksForListRecords = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: CREATING_TASKS_FOR_LIST_RECORDS
      });

      const response = await axios.post(
        '/v1/user/tasks/create_for_list_records',
        qs.stringify({ task: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(createTasksForListRecords(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_CREATING_TASKS_FOR_LIST_RECORDS,
        userCreatedTasks: {
          status: 'error',
          message: 'Error creating tasks for selected records'
        }
      });
    }
  };
}

export const FETCHING_AVAILABLE_LISTS = 'FETCHING_AVAILABLE_LISTS';
export const AVAILABLE_LISTS_FETCHED = 'AVAILABLE_LISTS_FETCHED';
export const ERROR_FETCHING_AVAILABLE_LISTS = 'ERROR_FETCHING_AVAILABLE_LISTS';

export function fetchUserAvailableLists(response) {
  return {
    type: AVAILABLE_LISTS_FETCHED,
    userAvailableLists: response
  }
}

export const handleFetchUserAvailableLists = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_AVAILABLE_LISTS
      });

      const response = await axios.get(
        '/v1/user/lists/available_lists',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchUserAvailableLists(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_AVAILABLE_LISTS,
        userAvailableLists: {
          status: 'error',
          message: 'Error fetching available lists'
        }
      });
    }
  };
}



export const FETCHING_LISTS_FOLDER = 'FETCHING_LISTS_FOLDER';
export const LISTS_FOLDER_FETCHED = 'LISTS_FOLDER_FETCHED';
export const ERROR_FETCHING_LISTS_FOLDER = 'ERROR_FETCHING_LISTS_FOLDER';

export function fetchListsFolderData(response) {
  return {
    type: LISTS_FOLDER_FETCHED,
    listsFolder: response
  }
}

export const handleFetchListsFolderData = (session, cancelToken, folderId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_LISTS_FOLDER
      });

      const response = await axios.get(
        `/v1/user/lists_folders/${folderId}`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchListsFolderData(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_LISTS_FOLDER,
        listsFolder: {
          status: 'error',
          message: 'Error fetching lists folder'
        }
      });
    }
  };
}

export const FETCHING_LISTS_FOLDER_LISTS = 'FETCHING_LISTS_FOLDER_LISTS';
export const LISTS_FOLDER_LISTS_FETCHED = 'LISTS_FOLDER_LISTS_FETCHED';
export const ERROR_FETCHING_LISTS_FOLDER_LISTS = 'ERROR_FETCHING_LISTS_FOLDER_LISTS';

export function fetchListsFolderLists(response) {
  return {
    type: LISTS_FOLDER_LISTS_FETCHED,
    listsFolderLists: response
  }
}

export const handleFetchListsFolderLists = (session, cancelToken, folderId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_LISTS_FOLDER_LISTS
      });

      const response = await axios.get(
        `/v1/user/lists_folders/${folderId}/lists`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchListsFolderLists(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_LISTS_FOLDER_LISTS,
        listsFolderLists: {
          status: 'error',
          message: 'Error fetching lists folder lists'
        }
      });
    }
  };
}

export const FETCHING_LISTS_FOLDER_RECENTLY_VIEWED_LISTS = 'FETCHING_LISTS_FOLDER_RECENTLY_VIEWED_LISTS';
export const LISTS_FOLDER_RECENTLY_VIEWED_LISTS_FETCHED = 'LISTS_FOLDER_RECENTLY_VIEWED_LISTS_FETCHED';
export const ERROR_FETCHING_LISTS_FOLDER_RECENTLY_VIEWED_LISTS = 'ERROR_FETCHING_LISTS_FOLDER_RECENTLY_VIEWED_LISTS';

export function fetchListsFolderRecentlyViewedLists(response) {
  return {
    type: LISTS_FOLDER_RECENTLY_VIEWED_LISTS_FETCHED,
    listsFolderRecentlyViewedLists: response
  }
}

export const handleFetchListsFolderRecentlyViewedLists = (session, cancelToken, folderId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_LISTS_FOLDER_RECENTLY_VIEWED_LISTS
      });

      const response = await axios.get(
        `/v1/user/lists_folders/${folderId}/recently_viewed_lists`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchListsFolderRecentlyViewedLists(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_LISTS_FOLDER_RECENTLY_VIEWED_LISTS,
        listsFolderRecentlyViewedLists: {
          status: 'error',
          message: 'Error fetching lists folder recently viewed lists'
        }
      });
    }
  };
}

export const SAVING_CREATE_NEW_CONTACT_PARAMS = 'SAVING_CREATE_NEW_CONTACT_PARAMS';
export const CREATE_NEW_CONTACT_PARAMS_SAVED = 'CREATE_NEW_CONTACT_PARAMS_SAVED';
export const ERROR_SAVING_CREATE_NEW_CONTACT_PARAMS = 'ERROR_SAVING_CREATE_NEW_CONTACT_PARAMS';

export function saveCreateNewContactParams(response) {
  return {
    type: CREATE_NEW_CONTACT_PARAMS_SAVED,
    createNewContactParamsSaved: response
  }
}

export const handleSaveCreateNewContactParams = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: SAVING_CREATE_NEW_CONTACT_PARAMS
      });

      let data = new FormData();
      data.append('contact[first_name]', params.first_name);
      data.append('contact[last_name]', params.last_name);
      data.append('contact[investor_id]', params.investor_id);
      data.append('contact[position_id]', params.position_id);
      data.append('contact[email]', params.email);
      data.append('contact[phone]', params.phone);
      data.append('contact[linkedin_url]', params.linkedin_url);
      data.append('contact[universities_ids]', params.universities_ids);
      data.append('contact[biography]', params.biography);
      data.append('contact[additional_notes]', params.additional_notes);

      if (params.photo != undefined) {
        data.append('contact[photo]', params.photo);
      }

      const response = await axios.post(
        `/v1/user/contacts`,
        data,
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(saveCreateNewContactParams(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_SAVING_CREATE_NEW_CONTACT_PARAMS,
        createNewContactParamsSaved: {
          status: 'error',
          message: 'Error saving New Contact params'
        }
      });
    }
  };
}

export const UPDATING_CONTACT_PARAMS = 'UPDATING_CONTACT_PARAMS';
export const CONTACT_PARAMS_UPDATED = 'CONTACT_PARAMS_UPDATED';
export const ERROR_UPDATING_CONTACT_PARAMS = 'ERROR_UPDATING_CONTACT_PARAMS';

export function updateContactParams(response) {
  return {
    type: CONTACT_PARAMS_UPDATED,
    contactParamsUpdated: response
  }
}

export const handleUpdateContactParams = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: UPDATING_CONTACT_PARAMS
      });

      let data = new FormData();
      data.append('contact[id]', params.id);
      data.append('contact[first_name]', params.first_name);
      data.append('contact[last_name]', params.last_name);
      data.append('contact[investor_id]', params.investor_id);
      data.append('contact[position_id]', params.position_id);
      data.append('contact[email]', params.email);
      data.append('contact[phone]', params.phone);
      data.append('contact[linkedin_url]', params.linkedin_url);
      data.append('contact[universities_ids]', params.universities_ids);
      data.append('contact[biography]', params.biography);
      data.append('contact[additional_notes]', params.additional_notes);

      if (params.photo != undefined && params.photo.name) {
        data.append('contact[photo]', params.photo);
      }

      const response = await axios.put(
        `/v1/user/contacts/${params.id}`,
        data,
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(updateContactParams(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_UPDATING_CONTACT_PARAMS,
        contactParamsUpdated: {
          status: 'error',
          message: 'Error updating Contact params'
        }
      });
    }
  };
}

export const FETCHING_ASSOCIATED_INVESTORS = 'FETCHING_ASSOCIATED_INVESTORS';
export const ASSOCIATED_INVESTORS_FETCHED = 'ASSOCIATED_INVESTORS_FETCHED';
export const ERROR_FETCHING_ASSOCIATED_INVESTORS = 'ERROR_FETCHING_ASSOCIATED_INVESTORS';

export function fetchAssociatedInvestors(response) {
  return {
    type: ASSOCIATED_INVESTORS_FETCHED,
    associatedInvestors: response
  }
}

export const handleFetchAssociatedInvestors = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_ASSOCIATED_INVESTORS
      });

      const response = await axios.get(
        `/v1/user/investors/0/associated_investors`,
        {
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchAssociatedInvestors(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_ASSOCIATED_INVESTORS,
        associatedInvestors: {
          status: 'error',
          message: 'Error fetching investors Types'
        }
      });
    }
  };
}

export const FETCHING_TITLES = 'FETCHING_TITLES';
export const TITLES_FETCHED = 'TITLES_FETCHED';
export const ERROR_FETCHING_TITLES = 'ERROR_FETCHING_TITLES';

export function fetchTitles(response) {
  return {
    type: TITLES_FETCHED,
    titles: response
  }
}

export const handleFetchTitles = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_TITLES
      });

      const response = await axios.get(
        `/v1/user/titles`,
        {
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchTitles(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_TITLES,
        titles: {
          status: 'error',
          message: 'Error fetching contact titles'
        }
      });
    }
  };
}

export const SAVING_CREATE_NEW_INVESTOR_PARAMS = 'SAVING_CREATE_NEW_INVESTOR_PARAMS';
export const CREATE_NEW_INVESTOR_PARAMS_SAVED = 'CREATE_NEW_INVESTOR_PARAMS_SAVED';
export const ERROR_SAVING_CREATE_NEW_INVESTOR_PARAMS = 'ERROR_SAVING_CREATE_NEW_INVESTOR_PARAMS';

export function saveCreateNewInvestorParams(response) {
  return {
    type: CREATE_NEW_INVESTOR_PARAMS_SAVED,
    createNewInvestorParamsSaved: response
  }
}

export const handleSaveCreateNewInvestorParams = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: SAVING_CREATE_NEW_INVESTOR_PARAMS
      });

      let data = new FormData();
      data.append('investor[name]', params.name);
      data.append('investor[investor_type_id]', params.investor_type_id);
      data.append('investor[company_email]', params.company_email);
      data.append('investor[phone]', params.phone);
      data.append('investor[invests_ins_ids]', params.invests_ins_ids);
      data.append('investor[website]', params.website);
      data.append('investor[firm_overview]', params.firm_overview);
      data.append('investor[address1]', params.address1);
      data.append('investor[address2]', params.address2);
      data.append('investor[city]', params.city);
      data.append('investor[city_id]', params.city_id);
      data.append('investor[state]', params.state);
      data.append('investor[country_id]', params.country_id);
      data.append('investor[zip_code]', params.zip_code);
      data.append('investor[aum_range_id]', params.aum_range_id);
      data.append('investor[founded_in]', params.founded_in);
      data.append('contact_ids', params.contact_ids);
      data.append('geographic_area_ids', params.geographic_areas);
      data.append('allocation_preferences', params.allocation_preferences);

      if (params.photo != undefined) {
        data.append('investor[photo]', params.photo);
      }

      const response = await axios.post(
        `/v1/user/investors`,
        data,
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(saveCreateNewInvestorParams(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_SAVING_CREATE_NEW_INVESTOR_PARAMS,
        createNewInvestorParamsSaved: {
          status: 'error',
          message: 'Error saving New Investor params'
        }
      });
    }
  };
}
export const UPDATING_INVESTOR_PARAMS = 'UPDATING_INVESTOR_PARAMS';
export const INVESTOR_PARAMS_UPDATED = 'INVESTOR_PARAMS_UPDATED';
export const ERROR_UPDATING_INVESTOR_PARAMS = 'ERROR_UPDATING_INVESTOR_PARAMS';

export function updateInvestorParams(response) {
  return {
    type: INVESTOR_PARAMS_UPDATED,
    investorParamsUpdated: response
  }
}

export const handleUpdateInvestorParams = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: UPDATING_INVESTOR_PARAMS
      });

      let data = new FormData();
      data.append('investor[id]', params.id);
      data.append('investor[name]', params.name);
      data.append('investor[investor_type_id]', params.investor_type_id);
      data.append('investor[company_email]', params.company_email);
      data.append('investor[phone]', params.phone);
      data.append('investor[invests_ins_ids]', params.invests_ins_ids);
      data.append('investor[website]', params.website);
      data.append('investor[firm_overview]', params.firm_overview);
      data.append('investor[address1]', params.address1);
      data.append('investor[address2]', params.address2);
      data.append('investor[city]', params.city);
      data.append('investor[city_id]', params.city_id);
      data.append('investor[state]', params.state);
      data.append('investor[country_id]', params.country_id);
      data.append('investor[zip_code]', params.zip_code);
      data.append('investor[aum_range_id]', params.aum_range_id);
      data.append('investor[founded_in]', params.founded_in);
      data.append('contact_ids', params.contact_ids);
      data.append('geographic_area_ids', params.geographic_areas);
      data.append('allocation_preferences', params.allocation_preferences);

      if (params.photo != undefined) {
        data.append('investor[photo]', params.photo);
      }

      const response = await axios.put(
        `/v1/user/investors/${params.id}`,
        data,
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(updateInvestorParams(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_UPDATING_INVESTOR_PARAMS,
        investorParamsUpdated: {
          status: 'error',
          message: 'Error updating Investor params'
        }
      });
    }
  };
}

export const FETCHING_SELECTED_INVESTOR = 'FETCHING_SELECTED_INVESTOR';
export const SELECTED_INVESTOR_FETCHED = 'SELECTED_INVESTOR_FETCHED';
export const ERROR_FETCHING_SELECTED_INVESTOR = 'ERROR_FETCHING_SELECTED_INVESTOR';

export function fetchSelectedInvestor(response) {
  return {
    type: SELECTED_INVESTOR_FETCHED,
    selectedInvestor: response
  }
}

export const handleFetchSelectedInvestor = (session, cancelToken, investorId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_SELECTED_INVESTOR
      });

      const response = await axios.get(
        `/v1/user/investors/${investorId}/selected_investor`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchSelectedInvestor(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_SELECTED_INVESTOR,
        selectedInvestor: {
          status: 'error',
          message: 'Error fetching investor'
        }
      });
    }
  };
}
export const FETCHING_OWNED_CONTACTS = 'FETCHING_OWNED_CONTACTS';
export const OWNED_CONTACTS_FETCHED = 'OWNED_CONTACTS_FETCHED';
export const ERROR_FETCHING_OWNED_CONTACTS = 'ERROR_FETCHING_OWNED_CONTACTS';

export function fetchOwnedContacts(response) {
  return {
    type: OWNED_CONTACTS_FETCHED,
    ownedContacts: response
  }
}

export const handleFetchOwnedContacts = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_OWNED_CONTACTS
      });

      const response = await axios.get(
        `/v1/user/contacts/owned_contacts`,
        {
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchOwnedContacts(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_OWNED_CONTACTS,
        ownedContacts: {
          status: 'error',
          message: 'Error fetching owned contacts'
        }
      });
    }
  };
}

export const FETCHING_SELECTED_CONTACT = 'FETCHING_SELECTED_CONTACT';
export const SELECTED_CONTACT_FETCHED = 'SELECTED_CONTACT_FETCHED';
export const ERROR_FETCHING_SELECTED_CONTACT = 'ERROR_FETCHING_SELECTED_CONTACT';

export function fetchSelectedContact(response) {
  return {
    type: SELECTED_CONTACT_FETCHED,
    selectedContact: response
  }
}

export const handleFetchSelectedContact = (session, cancelToken, contactId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_SELECTED_CONTACT
      });

      const response = await axios.get(
        `/v1/user/contacts/${contactId}/selected_contact`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchSelectedContact(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_SELECTED_CONTACT,
        selectedContact: {
          status: 'error',
          message: 'Error fetching contact'
        }
      });
    }
  };
}

export const FETCHING_INVESTOR_TYPES = 'FETCHING_INVESTOR_TYPES';
export const INVESTOR_TYPES_FETCHED = 'INVESTOR_TYPES_FETCHED';
export const ERROR_FETCHING_INVESTOR_TYPES = 'ERROR_FETCHING_INVESTOR_TYPES';

export function fetchInvestorTypes(response) {
  return {
    type: INVESTOR_TYPES_FETCHED,
    investorTypes: response
  }
}

export const handleFetchInvestorTypes = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_INVESTOR_TYPES
      });

      const response = await axios.get(
        `/v1/user/investors/investor_types`,
        {
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchInvestorTypes(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_INVESTOR_TYPES,
        investorTypes: {
          status: 'error',
          message: 'Error fetching investors Types'
        }
      });
    }
  };
}

export const FETCHING_COUNTRIES = 'FETCHING_COUNTRIES';
export const COUNTRIES_FETCHED = 'COUNTRIES_FETCHED';
export const ERROR_FETCHING_COUNTRIES = 'ERROR_FETCHING_COUNTRIES';

export function fetchCountries(response) {
  return {
    type: COUNTRIES_FETCHED,
    countries: response
  }
}

export const handleFetchCountries = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_COUNTRIES
      });

      const response = await axios.get(
        `/v1/user/countries`,
        {
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchCountries(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_COUNTRIES,
        countries: {
          status: 'error',
          message: 'Error fetching investors Types'
        }
      });
    }
  };
}

export const FETCHING_ASSET_CLASS_INTERESTS = 'FETCHING_ASSET_CLASS_INTERESTS';
export const ASSET_CLASS_INTERESTS_FETCHED = 'ASSET_CLASS_INTERESTS_FETCHED';
export const ERROR_FETCHING_ASSET_CLASS_INTERESTS = 'ERROR_FETCHING_ASSET_CLASS_INTERESTS';

export function fetchAssetClassInterests(response) {
  return {
    type: ASSET_CLASS_INTERESTS_FETCHED,
    assetClassInterests: response
  }
}

export const handleFetchAssetClassInterests = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_ASSET_CLASS_INTERESTS
      });

      const response = await axios.get(
        `/v1/user/investors/asset_class_focuses`,
        {
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchAssetClassInterests(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_ASSET_CLASS_INTERESTS,
        assetClassInterests: {
          status: 'error',
          message: 'Error fetching Asset Class Interests'
        }
      });
    }
  };
}

export const FETCHING_GEOGRAPHIC_AREAS = 'FETCHING_GEOGRAPHIC_AREAS';
export const GEOGRAPHIC_AREAS_FETCHED = 'GEOGRAPHIC_AREAS_FETCHED';
export const ERROR_FETCHING_GEOGRAPHIC_AREAS = 'ERROR_FETCHING_GEOGRAPHIC_AREAS';

export function fetchGeographicAreas(response) {
  return {
    type: GEOGRAPHIC_AREAS_FETCHED,
    geographicAreas: response
  }
}

export const handleFetchGeographicAreas = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_GEOGRAPHIC_AREAS
      });

      const response = await axios.get(
        `/v1/user/geographic_areas`,
        {
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchGeographicAreas(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_GEOGRAPHIC_AREAS,
        geographicAreas: {
          status: 'error',
          message: 'Error fetching Geographic Areas'
        }
      });
    }
  };
}

export const FETCHING_AUM_RANGES = 'FETCHING_AUM_RANGES';
export const AUM_RANGES_FETCHED = 'AUM_RANGES_FETCHED';
export const ERROR_FETCHING_AUM_RANGES = 'ERROR_FETCHING_AUM_RANGES';

export function fetchAumRanges(response) {
  return {
    type: AUM_RANGES_FETCHED,
    aumRanges: response
  }
}

export const handleFetchAumRanges = (session, cancelToken) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_AUM_RANGES
      });

      const response = await axios.get(
        `/v1/user/aum_ranges`,
        {
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchAumRanges(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_AUM_RANGES,
        aumRanges: {
          status: 'error',
          message: 'Error fetching Geographic Areas'
        }
      });
    }
  };
}

export const SAVE_SELECTED_LISTS = 'SAVE_SELECTED_LISTS';

export function saveSelectedLists(selectedLists) {
  return {
    type: SAVE_SELECTED_LISTS,
    selectedLists,
  }
}

export const handleSelectedLists = (selectedLists) => {
  return dispatch => {
    dispatch(saveSelectedLists(selectedLists))
  }
}

export const SAVE_SELECTED_LISTS_FOLDERS = 'SAVE_SELECTED_LISTS_FOLDERS';

export function saveSelectedListsFolders(selectedListsFolders) {
  return {
    type: SAVE_SELECTED_LISTS_FOLDERS,
    selectedListsFolders,
  }
}

export const handleSelectedListsFolders = (selectedListsFolders) => {
  return dispatch => {
    dispatch(saveSelectedListsFolders(selectedListsFolders))
  }
}

export const SAVE_SELECTED_CONTACTS = 'SAVE_SELECTED_CONTACTS';

export function saveSelectedContacts(selectedContacts) {
  return {
    type: SAVE_SELECTED_CONTACTS,
    selectedContacts,
  }
}

export const handleSelectedContacts = (selectedContacts) => {
  return dispatch => {
    dispatch(saveSelectedContacts(selectedContacts))
  }
}

export const ADDING_LISTS_TO_FOLDER = 'ADDING_LISTS_TO_FOLDER';
export const LISTS_TO_FOLDER_ADDED = 'LISTS_TO_FOLDER_ADDED';
export const ERROR_ADDING_LISTS_TO_FOLDER = 'ERROR_ADDING_LISTS_TO_FOLDER';

export function addListsToFolder(response) {
  return {
    type: LISTS_TO_FOLDER_ADDED,
    addListsToFolder: response
  }
}

export const handleAddListsToFolder = (session, cancelToken, listsFolderId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: ADDING_LISTS_TO_FOLDER
      });

      const response = await axios.post(
        `/v1/user/lists_folders/${listsFolderId}/add_lists`,
        qs.stringify({ lists_folder: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(addListsToFolder(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_ADDING_LISTS_TO_FOLDER,
        addListsToFolder: {
          status: 'error',
          message: 'Error adding lists to folder'
        }
      });
    }
  };
}

export const FETCHING_INVESTOR_INVESTMENTS = 'FETCHING_INVESTOR_INVESTMENTS';
export const INVESTOR_INVESTMENTS_FETCHED = 'INVESTOR_INVESTMENTS_FETCHED';
export const ERROR_FETCHING_INVESTOR_INVESTMENTS = 'ERROR_FETCHING_INVESTOR_INVESTMENTS';

export function fetchInvestorInvestments(response) {
  return {
    type: INVESTOR_INVESTMENTS_FETCHED,
    investorInvestments: response
  }
}

export const handleFetchInvestorInvestments = (session, cancelToken, familyOfficeId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_INVESTOR_INVESTMENTS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/investments`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchInvestorInvestments(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_INVESTOR_INVESTMENTS,
        investorInvestments: {
          status: 'error',
          message: 'Error fetching investor investments'
        }
      });
    }
  };
}

export const FETCHING_INVESTOR_INVESTMENTS_NEWS = 'FETCHING_INVESTOR_INVESTMENTS_NEWS';
export const INVESTOR_INVESTMENTS_NEWS_FETCHED = 'INVESTOR_INVESTMENTS_NEWS_FETCHED';
export const ERROR_FETCHING_INVESTOR_INVESTMENTS_NEWS = 'ERROR_FETCHING_INVESTOR_INVESTMENTS_NEWS';

export function fetchInvestorInvestmentsNews(response) {
  return {
    type: INVESTOR_INVESTMENTS_NEWS_FETCHED,
    investorInvestmentsNews: response
  }
}

export const handleFetchInvestorInvestmentsNews = (session, cancelToken, familyOfficeId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_INVESTOR_INVESTMENTS_NEWS
      });

      const response = await axios.get(
        `/v1/user/investors/${familyOfficeId}/investments_news`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchInvestorInvestmentsNews(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_INVESTOR_INVESTMENTS_NEWS,
        investorInvestmentsNews: {
          status: 'error',
          message: 'Error fetching investor investments news'
        }
      });
    }
  };
}

////////////
export const SAVE_FOS_LIST_SORT_VALUES = 'SAVE_FOS_LIST_SORT_VALUES';

export function saveFOsListSortValues(familyOfficesListSortValues) {
  return {
    type: SAVE_FOS_LIST_SORT_VALUES,
    familyOfficesListSortValues,
  }
}

export const handleFOsListSortValues = (familyOfficesListSortValues) => {
  return dispatch => {
    dispatch(saveFOsListSortValues(familyOfficesListSortValues))
  }
}

export const SAVE_CONTACTS_LIST_SORT_VALUES = 'SAVE_CONTACTS_LIST_SORT_VALUES';

export function saveContactsListSortValues(contactsListSortValues) {
  return {
    type: SAVE_CONTACTS_LIST_SORT_VALUES,
    contactsListSortValues,
  }
}

export const handleContactsListSortValues = (contactsListSortValues) => {
  return dispatch => {
    dispatch(saveContactsListSortValues(contactsListSortValues))
  }
}

export const SAVE_LISTS_AND_FOLDERS_PAGE_TAB = 'SAVE_LISTS_AND_FOLDERS_PAGE_TAB';

export function saveListAndFoldersPageTab(listsAndFoldersPageTab) {
  return {
    type: SAVE_LISTS_AND_FOLDERS_PAGE_TAB,
    listsAndFoldersPageTab,
  }
}

export const handleListAndFoldersPageTab = (listsAndFoldersPageTab) => {
  return dispatch => {
    dispatch(saveListAndFoldersPageTab(listsAndFoldersPageTab))
  }
}

///////

export const SAVE_LISTS_SORT = 'SAVE_LISTS_SORT';

export function saveListsSort(listsSort) {
  return {
    type: SAVE_LISTS_SORT,
    listsSort,
  }
}

export const handleListsSort = (listsSort) => {
  return dispatch => {
    dispatch(saveListsSort(listsSort))
  }
}

export const SAVE_NOTES_SORT = 'SAVE_NOTES_SORT';

export function saveNotesSort(notesSort) {
  return {
    type: SAVE_NOTES_SORT,
    notesSort,
  }
}

export const handleNotesSort = (notesSort) => {
  return dispatch => {
    dispatch(saveNotesSort(notesSort))
  }
}

export const SAVE_TASKS_SORT = 'SAVE_TASKS_SORT';

export function saveTasksSort(tasksSort) {
  return {
    type: SAVE_TASKS_SORT,
    tasksSort,
  }
}

export const handleTasksSort = (tasksSort) => {
  return dispatch => {
    dispatch(saveTasksSort(tasksSort))
  }
}

export const SAVE_FULL_PROFILE_CONTACTS_SORT = 'SAVE_FULL_PROFILE_CONTACTS_SORT';

export function saveFullProfileContactsSort(fullProfileContactsSort) {
  return {
    type: SAVE_FULL_PROFILE_CONTACTS_SORT,
    fullProfileContactsSort,
  }
}

export const handleFullProfileContactsSort = (fullProfileContactsSort) => {
  return dispatch => {
    dispatch(saveFullProfileContactsSort(fullProfileContactsSort))
  }
}

export const SAVE_SELECTED_TASKS = 'SAVE_SELECTED_TASKS';

export function saveSelectedTasks(selectedTasks) {
  return {
    type: SAVE_SELECTED_TASKS,
    selectedTasks,
  }
}

export const handleSelectedTasks = (selectedTasks) => {
  return dispatch => {
    dispatch(saveSelectedTasks(selectedTasks))
  }
}

export const SAVE_SELECTED_NOTES = 'SAVE_SELECTED_NOTES';

export function saveSelectedNotes(selectedNotes) {
  return {
    type: SAVE_SELECTED_NOTES,
    selectedNotes,
  }
}

export const handleSelectedNotes = (selectedNotes) => {
  return dispatch => {
    dispatch(saveSelectedNotes(selectedNotes))
  }
}

export const SAVE_ACTIVITY_REPORTS_PAGE_TAB = 'SAVE_ACTIVITY_REPORTS_PAGE_TAB';

export function saveActivityReportsPageTab(activityReportsPageTab) {
  return {
    type: SAVE_ACTIVITY_REPORTS_PAGE_TAB,
    activityReportsPageTab,
  }
}

export const handleActivityReportsPageTab = (activityReportsPageTab) => {
  return dispatch => {
    dispatch(saveActivityReportsPageTab(activityReportsPageTab))
  }
}


export const SAVE_SELECTED_TASKS_FILTERS = 'SAVE_SELECTED_TASKS_FILTERS';

export function saveSelectedTasksFilters(selectedTasksFilters) {
  return {
    type: SAVE_SELECTED_TASKS_FILTERS,
    selectedTasksFilters,
  }
}

export const handleSelectedTasksFilters = (selectedTasksFilters) => {
  return dispatch => {
    dispatch(saveSelectedTasksFilters(selectedTasksFilters))
  }
}

export const SAVE_SELECTED_NOTES_FILTERS = 'SAVE_SELECTED_NOTES_FILTERS';

export function saveSelectedNotesFilters(selectedNotesFilters) {
  return {
    type: SAVE_SELECTED_NOTES_FILTERS,
    selectedNotesFilters,
  }
}

export const handleSelectedNotesFilters = (selectedNotesFilters) => {
  return dispatch => {
    dispatch(saveSelectedNotesFilters(selectedNotesFilters))
  }
}

export const ARCHIVING_TASK = 'ARCHIVING_TASK';
export const TASK_ARCHIVED = 'TASK_ARCHIVED';
export const ERROR_ARCHIVING_TASK = 'ERROR_ARCHIVING_TASK';

export function archiveTask(response) {
  return {
    type: TASK_ARCHIVED,
    taskArchived: response
  }
}

export const handleArchiveTask = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: ARCHIVING_TASK
      });

      const response = await axios.put(
        `/v1/user/tasks/archive`,
        qs.stringify({ task: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(archiveTask(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_ARCHIVING_TASK,
        taskArchived: {
          status: 'error',
          message: 'Error archiving tasks'
        }
      });
    }
  };
}


export const ARCHIVING_NOTE = 'ARCHIVING_NOTE';
export const NOTE_ARCHIVED = 'NOTE_ARCHIVED';
export const ERROR_ARCHIVING_NOTE = 'ERROR_ARCHIVING_NOTE';

export function archiveNote(response) {
  return {
    type: NOTE_ARCHIVED,
    noteArchived: response
  }
}

export const handleArchiveNote = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: ARCHIVING_NOTE
      });

      const response = await axios.put(
        `/v1/user/notes/archive`,
        qs.stringify({ note: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(archiveNote(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_ARCHIVING_NOTE,
        noteArchived: {
          status: 'error',
          message: 'Error archiving notes'
        }
      });
    }
  };
}

export const MARKING_COMPLETE_TASK = 'MARKING_COMPLETE_TASK';
export const TASK_MARKED_COMPLETE = 'TASK_MARKED_COMPLETE';
export const ERROR_MARKING_COMPLETE_TASK = 'ERROR_MARKING_COMPLETE_TASK';

export function markCompleteTask(response) {
  return {
    type: TASK_MARKED_COMPLETE,
    taskMarkedComplete: response
  }
}

export const handleMarkCompleteTask = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: MARKING_COMPLETE_TASK
      });

      const response = await axios.put(
        `/v1/user/tasks/mark_complete`,
        qs.stringify({ task: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(markCompleteTask(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_MARKING_COMPLETE_TASK,
        taskMarkedComplete: {
          status: 'error',
          message: 'Error marking tasks as complete'
        }
      });
    }
  };
}

export const MARKING_INCOMPLETE_TASK = 'MARKING_INCOMPLETE_TASK';
export const TASK_MARKED_INCOMPLETE = 'TASK_MARKED_INCOMPLETE';
export const ERROR_MARKING_INCOMPLETE_TASK = 'ERROR_MARKING_INCOMPLETE_TASK';

export function markIncompleteTask(response) {
  return {
    type: TASK_MARKED_INCOMPLETE,
    taskMarkedIncomplete: response
  }
}

export const handleMarkIncompleteTask = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: MARKING_INCOMPLETE_TASK
      });

      const response = await axios.put(
        `/v1/user/tasks/mark_incomplete`,
        qs.stringify({ task: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(markIncompleteTask(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_MARKING_INCOMPLETE_TASK,
        taskMarkedIncomplete: {
          status: 'error',
          message: 'Error marking tasks as incomplete'
        }
      });
    }
  };
}

export const CHANGING_NOTE_TYPE = 'CHANGING_NOTE_TYPE';
export const CHANGED_NOTE_TYPE = 'CHANGED_NOTE_TYPE';
export const ERROR_CHANGING_NOTE_TYPE = 'ERROR_CHANGING_NOTE_TYPE';

export function changeNoteType(response) {
  return {
    type: CHANGED_NOTE_TYPE,
    noteTypeChanged: response
  }
}

export const handleChangeNoteType = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: CHANGING_NOTE_TYPE
      });

      const response = await axios.put(
        `/v1/user/notes/change_type`,
        qs.stringify({ note: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(changeNoteType(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_CHANGING_NOTE_TYPE,
        noteTypeChanged: {
          status: 'error',
          message: 'Error change note type'
        }
      });
    }
  };
}

export const CHANGING_TASK_PRIORITY = 'CHANGING_TASK_PRIORITY';
export const CHANGED_TASK_PRIORITY = 'CHANGED_TASK_PRIORITY';
export const ERROR_CHANGING_TASK_PRIORITY = 'ERROR_CHANGING_TASK_PRIORITY';

export function changePriorityTask(response) {
  return {
    type: CHANGED_TASK_PRIORITY,
    taskPriorityChanged: response
  }
}

export const handleChangePriorityTask = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: CHANGING_TASK_PRIORITY
      });

      const response = await axios.put(
        `/v1/user/tasks/change_priority`,
        qs.stringify({ task: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(changePriorityTask(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_CHANGING_TASK_PRIORITY,
        taskPriorityChanged: {
          status: 'error',
          message: 'Error change task priority'
        }
      });
    }
  };
}

export const CHANGING_TASK_DUE_DATE = 'CHANGING_TASK_DUE_DATE';
export const CHANGED_TASK_DUE_DATE = 'CHANGED_TASK_DUE_DATE';
export const ERROR_CHANGING_TASK_DUE_DATE = 'ERROR_CHANGING_TASK_DUE_DATE';

export function changeDueDateTask(response) {
  return {
    type: CHANGED_TASK_DUE_DATE,
    taskDueDateChanged: response
  }
}

export const handleChangeDueDateTask = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: CHANGING_TASK_DUE_DATE
      });

      const response = await axios.put(
        `/v1/user/tasks/change_due_date`,
        qs.stringify({ task: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(changeDueDateTask(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_CHANGING_TASK_DUE_DATE,
        taskDueDateChanged: {
          status: 'error',
          message: 'Error change task priority'
        }
      });
    }
  };
}
///////
export const SAVE_DATA_MANAGEMENT_PAGE_TAB = 'SAVE_DATA_MANAGEMENT_PAGE_TAB';

export function saveDataManagementPageTab(dataManagementPageTab) {
  return {
    type: SAVE_DATA_MANAGEMENT_PAGE_TAB,
    dataManagementPageTab,
  }
}

export const handleDataManagementPageTab = (dataManagementPageTab) => {
  return dispatch => {
    dispatch(saveDataManagementPageTab(dataManagementPageTab))
  }
}

export const SAVE_SELECTED_ENTITIES = 'SAVE_SELECTED_ENTITIES';

export function saveSelectedEntities(selectedEntities) {
  return {
    type: SAVE_SELECTED_ENTITIES,
    selectedEntities,
  }
}

export const handleSelectedEntities = (selectedEntities) => {
  return dispatch => {
    dispatch(saveSelectedEntities(selectedEntities))
  }
}

export const SAVE_DATA_MANAGEMENT_SELECTED_CONTACTS = 'SAVE_DATA_MANAGEMENT_SELECTED_CONTACTS';

export function saveDataManagementSelectedContacts(selectedDataManagementContacts) {
  return {
    type: SAVE_DATA_MANAGEMENT_SELECTED_CONTACTS,
    selectedDataManagementContacts,
  }
}

export const handleSelectedDataManagementContacts = (selectedDataManagementContacts) => {
  return dispatch => {
    dispatch(saveDataManagementSelectedContacts(selectedDataManagementContacts))
  }
}

export const FETCHING_DATA_MANAGEMENT_CONTACTS = 'FETCHING_DATA_MANAGEMENT_CONTACTS';
export const DATA_MANAGEMENT_CONTACTS_FETCHED = 'DATA_MANAGEMENT_CONTACTS_FETCHED';
export const ERROR_FETCHING_DATA_MANAGEMENT_CONTACTS = 'ERROR_FETCHING_DATA_MANAGEMENT_CONTACTS';

export function fetchDataManagementContacts(response) {
  return {
    type: DATA_MANAGEMENT_CONTACTS_FETCHED,
    dataManagementContacts: response
  }
}

export const handleFetchDataManagementContacts = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_DATA_MANAGEMENT_CONTACTS
      });

      const response = await axios.get(
        '/v1/user/contacts/team_contacts',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchDataManagementContacts(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_DATA_MANAGEMENT_CONTACTS,
        dataManagementContacts: {
          status: 'error',
          message: 'Error fetching data management contacts'
        }
      });
    }
  };
}

export const FETCHING_DATA_MANAGEMENT_ENTITIES = 'FETCHING_DATA_MANAGEMENT_ENTITIES';
export const DATA_MANAGEMENT_ENTITIES_FETCHED = 'DATA_MANAGEMENT_ENTITIES_FETCHED';
export const ERROR_FETCHING_DATA_MANAGEMENT_ENTITIES = 'ERROR_FETCHING_DATA_MANAGEMENT_ENTITIES';

export function fetchDataManagementEntities(response) {
  return {
    type: DATA_MANAGEMENT_ENTITIES_FETCHED,
    dataManagementEntities: response
  }
}

export const handleFetchDataManagementEntities = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_DATA_MANAGEMENT_ENTITIES
      });

      const response = await axios.get(
        '/v1/user/investors/team_entities',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchDataManagementEntities(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_DATA_MANAGEMENT_ENTITIES,
        dataManagementEntities: {
          status: 'error',
          message: 'Error fetching data management entities'
        }
      });
    }
  };
}

/////
export const SAVE_ENTITIES_SORT = 'SAVE_ENTITIES_SORT';

export function saveEntitiesSort(entitiesSort) {
  return {
    type: SAVE_ENTITIES_SORT,
    entitiesSort,
  }
}

export const handleEntitiesSort = (entitiesSort) => {
  return dispatch => {
    dispatch(saveEntitiesSort(entitiesSort))
  }
}


export const SAVE_DATA_MANAGEMENT_CONTACT_SORT = 'SAVE_DATA_MANAGEMENT_CONTACT_SORT';

export function saveDataManagementeContactSort(dataManagementContactsSort) {
  return {
    type: SAVE_DATA_MANAGEMENT_CONTACT_SORT,
    dataManagementContactsSort,
  }
}
export const handleDataManagementContactsSort = (dataManagementContactsSort) => {
  return dispatch => {
    dispatch(saveDataManagementeContactSort(dataManagementContactsSort))
  }
}

export const ARCHIVING_ENTITY = 'ARCHIVING_ENTITY';
export const ENTITY_ARCHIVED = 'ENTITY_ARCHIVED';
export const ERROR_ARCHIVING_ENTITY = 'ERROR_ARCHIVING_ENTITY';

export function archiveEntity(response) {
  return {
    type: ENTITY_ARCHIVED,
    entityArchived: response
  }
}

export const handleArchiveEntity = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: ARCHIVING_ENTITY
      });

      const response = await axios.put(
        `/v1/user/investors/archive_entities`,
        qs.stringify({ investor: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(archiveEntity(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_ARCHIVING_ENTITY,
        entityArchived: {
          status: 'error',
          message: 'Error archiving tasks'
        }
      });
    }
  };
}

export const ARCHIVING_DATA_MANAGEMENT_CONTACT = 'ARCHIVING_DATA_MANAGEMENT_CONTACT';
export const DATA_MANAGEMENT_CONTACT_ARCHIVED = 'DATA_MANAGEMENT_CONTACT_ARCHIVED';
export const ERROR_ARCHIVING_DATA_MANAGEMENT_CONTACT = 'ERROR_ARCHIVING_DATA_MANAGEMENT_CONTACT';

export function archiveDataManagementContact(response) {
  return {
    type: DATA_MANAGEMENT_CONTACT_ARCHIVED,
    dataManagementContactArchived: response
  }
}

export const handleArchiveDataManagementContact = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: ARCHIVING_DATA_MANAGEMENT_CONTACT
      });

      const response = await axios.put(
        `/v1/user/contacts/archive_data_management_contacts`,
        qs.stringify({ contact: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(archiveDataManagementContact(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_ARCHIVING_DATA_MANAGEMENT_CONTACT,
        dataManagementContactArchived: {
          status: 'error',
          message: 'Error archiving tasks'
        }
      });
    }
  };
}

export const EXPORTING_DATA_MANAGEMENT_ITEMS = 'EXPORTING_DATA_MANAGEMENT_ITEMS';
export const DATA_MANAGEMENT_ITEMS_EXPORTED = 'DATA_MANAGEMENT_ITEMS_EXPORTED';
export const ERROR_EXPORTING_DATA_MANAGEMENT_ITEMS = 'ERROR_EXPORTING_DATA_MANAGEMENT_ITEMS';

export function exportDataManagementItems(response) {
  return {
    type: DATA_MANAGEMENT_ITEMS_EXPORTED,
    exportedDataManagementItems: response
  }
}

export const handleExportDataManagementItems = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: EXPORTING_DATA_MANAGEMENT_ITEMS
      });

      const response = await axios.post(
        `/v1/user/data_managements/export_items`,
        qs.stringify({ export_data: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(exportDataManagementItems(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_EXPORTING_DATA_MANAGEMENT_ITEMS,
        exportedDataManagementItems: {
          status: 'error',
          message: 'Error exporting selected data management items'
        }
      });
    }
  };
}
///////
export const REMOVING_LISTS_FROM_FOLDER = 'REMOVING_LISTS_FROM_FOLDER';
export const LISTS_FROM_FOLDER_REMOVED = 'LISTS_FROM_FOLDER_REMOVED';
export const ERROR_REMOVING_LISTS_FROM_FOLDER = 'ERROR_REMOVING_LISTS_FROM_FOLDER';

export function removeListsFromFolder(response) {
  return {
    type: LISTS_FROM_FOLDER_REMOVED,
    removeListsFromFolder: response
  }
}

export const handleRemoveListsFromFolder = (session, cancelToken, listsFolderId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: REMOVING_LISTS_FROM_FOLDER
      });

      const response = await axios.delete(
        `/v1/user/lists_folders/${listsFolderId}/remove_lists`,
        {
          params: { lists_folder: params },
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(removeListsFromFolder(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_REMOVING_LISTS_FROM_FOLDER,
        removeListsFromFolder: {
          status: 'error',
          message: 'Error removing lists from folder'
        }
      });
    }
  };
}

export const GENERATING_DOSSIER_LIST_PDF = 'GENERATING_DOSSIER_LIST_PDF';
export const DOSSIER_GENERATED_LIST_PDF = 'DOSSIER_GENERATED_LIST_PDF';
export const ERROR_GENERATING_DOSSIER_LIST_PDF = 'ERROR_GENERATING_DOSSIER_LIST_PDF';

export function generateDossierListPDF(response) {
  return {
    type: DOSSIER_GENERATED_LIST_PDF,
    listDossierResponse: response
  }
}

export const handleGenerateDossierListPDF = (session, cancelToken, listId, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: GENERATING_DOSSIER_LIST_PDF
      });

      const response = await axios.get(
        `/v1/user/dossiers/dossiers/${listId}`,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(generateDossierListPDF(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_GENERATING_DOSSIER_LIST_PDF,
        listDossierResponse: {
          status: 'error',
          message: 'Error generating pdf'
        }
      });
    }
  };
}


export const FETCHING_NOTES_OWNERS = 'FETCHING_NOTES_OWNERS';
export const NOTES_OWNERS_FETCHED = 'NOTES_OWNERS_FETCHED';
export const ERROR_FETCHING_NOTES_OWNERS = 'ERROR_FETCHING_NOTES_OWNERS';

export function fetchNotesOwners(response) {
  return {
    type: NOTES_OWNERS_FETCHED,
    notesOwners: response
  }
}

export const handleFetchNotesOwners = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_NOTES_OWNERS
      });

      const response = await axios.get(
        '/v1/user/activity_reports/notes_owners',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchNotesOwners(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_NOTES_OWNERS,
        notesOwners: {
          status: 'error',
          message: 'Error fetching notes owners'
        }
      });
    }
  };
}

export const FETCHING_TASKS_ASSIGNEES = 'FETCHING_TASKS_ASSIGNEES';
export const TASKS_ASSIGNEES_FETCHED = 'TASKS_ASSIGNEES_FETCHED';
export const ERROR_FETCHING_TASKS_ASSIGNEES = 'ERROR_FETCHING_TASKS_ASSIGNEES';

export function fetchTasksAssignees(response) {
  return {
    type: TASKS_ASSIGNEES_FETCHED,
    tasksAssignees: response
  }
}

export const handleFetchTasksAssignees = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_TASKS_ASSIGNEES
      });

      const response = await axios.get(
        '/v1/user/activity_reports/tasks_assignees',
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchTasksAssignees(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_TASKS_ASSIGNEES,
        tasksAssignees: {
          status: 'error',
          message: 'Error fetching tasks assignees'
        }
      });
    }
  };
}

export const SAVING_HIGHLIGHT = 'SAVING_HIGHLIGHT';
export const HIGHLIGHT_SAVED = 'HIGHLIGHT_SAVED';
export const ERROR_SAVING_HIGHLIGHT = 'ERROR_SAVING_HIGHLIGHT';

export function saveHighlight(response) {
  return {
    type: HIGHLIGHT_SAVED,
    highlightSaved: response
  }
}

export const handleSaveHighlight = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: SAVING_HIGHLIGHT
      });

      const response = await axios.post(
        `/v1/user/highlights`,
        qs.stringify({ highlight: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(saveHighlight(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_SAVING_HIGHLIGHT,
        highlightSaved: {
          status: 'error',
          message: 'Error saving selected highlight'
        }
      });
    }
  };
}

export const FETCHING_CONTACT_HIGHLIGHTS = 'FETCHING_CONTACT_HIGHLIGHTS';
export const CONTACT_HIGHLIGHTS_FETCHED = 'CONTACT_HIGHLIGHTS_FETCHED';
export const ERROR_FETCHING_CONTACT_HIGHLIGHTS = 'ERROR_FETCHING_CONTACT_HIGHLIGHTS';

export function fetchContactHighlights(response, contactId) {
  return {
    type: CONTACT_HIGHLIGHTS_FETCHED,
    contactHighlights: response,
    contactId: contactId
  }
}

export const handleFetchContactHighlights = (session, cancelToken, contactId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_CONTACT_HIGHLIGHTS,
        contactId
      });

      const response = await axios.get(
        `/v1/user/highlights/${contactId}/contact_highlights`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchContactHighlights(response.data, contactId));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_CONTACT_HIGHLIGHTS,
        contactHighlights: {
          status: 'error',
          message: 'Error fetching contact highlights'
        }
      });
    }
  };
}

export const FETCHING_FAMILY_OFFICE_HIGHLIGHTS = 'FETCHING_FAMILY_OFFICE_HIGHLIGHTS';
export const FAMILY_OFFICE_HIGHLIGHTS_FETCHED = 'FAMILY_OFFICE_HIGHLIGHTS_FETCHED';
export const ERROR_FETCHING_FAMILY_OFFICE_HIGHLIGHTS = 'ERROR_FETCHING_FAMILY_OFFICE_HIGHLIGHTS';

export function fetchFamilyOfficeHighlights(response) {
  return {
    type: FAMILY_OFFICE_HIGHLIGHTS_FETCHED,
    familyOfficeHighlights: response
  }
}

export const handleFetchFamilyOfficeHighlights = (session, cancelToken, familyOfficeId) => {
  return async dispatch => {
    try {
      dispatch({
        type: FETCHING_FAMILY_OFFICE_HIGHLIGHTS
      });

      const response = await axios.get(
        `/v1/user/highlights/${familyOfficeId}/family_office_highlights`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken,
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(fetchFamilyOfficeHighlights(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_FETCHING_FAMILY_OFFICE_HIGHLIGHTS,
        familyOfficeHighlights: {
          status: 'error',
          message: 'Error fetching contact highlights'
        }
      });
    }
  };
}

export const REMOVING_HIGHLIGHT = 'REMOVING_HIGHLIGHT';
export const HIGHLIGHT_REMOVED = 'HIGHLIGHT_REMOVED';
export const ERROR_REMOVING_HIGHLIGHT = 'ERROR_REMOVING_HIGHLIGHT';

export function removeHighlight(response) {
  return {
    type: HIGHLIGHT_REMOVED,
    highlightRemoved: response
  }
}

export const handleRemoveHighlight = (session, cancelToken, highlightId) => {
  return async dispatch => {
    try {
      dispatch({
        type: REMOVING_HIGHLIGHT
      });
      const response = await axios.delete(
        `/v1/user/highlights/${highlightId}`,
        {
          headers: {
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
          cancelToken
        }
      );

      dispatch(removeHighlight(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_REMOVING_HIGHLIGHT,
        highlightRemoved: {
          status: 'error',
          message: 'Error removing highight'
        }
      });
    }
  };
}

export const SAVING_RESEARCH_REQUEST_PARAMS = 'SAVING_RESEARCH_REQUEST_PARAMS';
export const RESEARCH_REQUEST_PARAMS_SAVED = 'RESEARCH_REQUEST_PARAMS_SAVED';
export const ERROR_SAVING_RESEARCH_REQUEST_PARAMS = 'ERROR_SAVING_RESEARCH_REQUEST_PARAMS';

export function saveResearchRequestParams(response) {
  return {
    type: RESEARCH_REQUEST_PARAMS_SAVED,
    researchRequestParamsSaved: response
  }
}

export const handleSaveResearchRequestParams = (session, cancelToken, params = {}) => {
  return async dispatch => {
    try {
      dispatch({
        type: SAVING_RESEARCH_REQUEST_PARAMS
      });

      const response = await axios.post(
        `/v1/user/research_requests`,
        qs.stringify({ research_request: _.isEmpty(params) ? {} : JSON.stringify(params) }),
        {
          cancelToken,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Email': session.email,
            'X-User-Token': session.token,
            'X-User-Admin-Log-Token': session.admin_auth_token,
          },
        }
      ).catch(function (thrown) {
        if (axios.isCancel(thrown)) {
          console.log('Request canceled', thrown.message);
        } else {

        }
      });

      dispatch(saveResearchRequestParams(response.data));
    } catch (e) {
      dispatch({
        type: ERROR_SAVING_RESEARCH_REQUEST_PARAMS,
        researchRequestParamsSaved: {
          status: 'error',
          message: 'Error saving research request params'
        }
      });
    }
  };
}
